import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useContext, useMemo } from "react";
import ReactDOM from "react-dom";
import Webcam from "react-webcam";
import { Loader, Text, Button, useMantineTheme } from "@mantine/core";
import useDocumentDetection, { resolveCaptureMode } from "../../../hooks/useDocumentDetection";
import useLocale from "../../../hooks/useLocale";
import useIsLandscape, { useDeclareLandscapeSupport, currentOrientation } from "../../../hooks/useOrientation";
import { DataContext } from "../../../context/DataContext";
import { resolveOnAccentColor, contrastingForeground } from "../../../utils/onAccentColor";
import { normalizeColor, resolveCheckmarkColor } from "../../../helpers/component-styles";
import { flagEnabled, ID_DOC_BEST_FRAME, ID_DOC_AUTO_CAPTURE } from "../../../utils/featureFlags";
import AndroidCameraBlockPortal from "../../../components/AndroidCameraBlockPortal";
import { getAndroidBlockVariant, shouldShowAndroidCameraBlock } from "../../../utils/androidDetection";
import { trackEvent } from "../../../utils/telemetry";
import { ReactComponent as VeratadLogo } from "../../../assets/veratad_ai_white_logo_large.svg";
import {
  IconCameraOff,
  IconX,
  IconHelp,
  IconCheck,
  IconSunHigh,
  IconId,
  IconCapture,
} from "@tabler/icons-react";

// Build stamp on every v2 telemetry event (see telemetryBase below).
export const CAPTURE_BUILD_V2 = "v2d";

// JPEG quality for the uploaded ID images (was 0.97, fallbacks 1.0).
// Matches the Microblink path: the Aug-Sep 2026 upload-compression
// A/B at 0.87 cut upload size 73% and median upload time 51% with
// PASS rate unchanged-to-better. Smaller files also shorten the
// encode itself, which the Android v2 shutter waits on.
const ID_DOC_JPEG_QUALITY = 0.87;
const STYLE_ID = "veratad-doc-capture-styles";

// ── Palette (from the "ID Capture Variants" design, variant A) ──
// The capture chrome is always dark regardless of the publisher's color
// scheme: it frames a live camera feed, and the accent (publisher primary)
// is the only brand color on screen.
const BG = "#0E0F10";
const FG = "#F4F5F2";
const MUTED = "#A7ADA9";
const GUIDE_TEXT = "#C9CEC9";
const GREEN = "#5BD68A";
const AMBER = "#F2C94C";
const STAGE_FALLBACK = "#221A14";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";
// Shared with the intro screen (setup-stage.js) so the "what you are
// about to see" preview is drawn from the same palette as the capture.
export const CAPTURE_BG = BG;
export const CAPTURE_FG = FG;
export const CAPTURE_GUIDE_TEXT = GUIDE_TEXT;
export const CAPTURE_STAGE_FALLBACK = STAGE_FALLBACK;

// Landscape side panel. 250px was fixed; translations run up to ~1.6x
// the English length (German, Finnish, Russian...) and at 250px the
// heading, cue and shutter captions stacked taller than a phone held
// sideways is. The panel now takes a share of the viewport width (a
// phone sideways has width to spare and height to lose) between the old
// 250px and 340px; the camera stage keeps the rest and its guide frame
// is height-bound in landscape anyway, so it loses nothing.
const SIDE_PANEL_MIN_W = 250;
const SIDE_PANEL_MAX_W = 340;
const SIDE_PANEL_VW = 36;
const SIDE_PANEL_WIDTH_CSS = `clamp(${SIDE_PANEL_MIN_W}px, ${SIDE_PANEL_VW}vw, ${SIDE_PANEL_MAX_W}px)`;
// Long words (Finnish "Henkilöllisyystodistuksen", German compounds) must
// break rather than push a column wider than its box. Applied to every
// line of copy on the screen. Body copy (the cue's detail line, the
// captions under the shutter, the help tips) also hyphenates where the
// browser has a dictionary for the session language (the root carries
// `lang`); headings and buttons do not, so a short title never wraps
// with a hyphen in it.
const WRAP_TEXT = { overflowWrap: "anywhere", wordBreak: "break-word" };
const WRAP_BODY = { ...WRAP_TEXT, hyphens: "auto", WebkitHyphens: "auto" };
// Assisted mode: the shutter is held while the quality gates fail, but
// only this long. After it the button is available regardless (best
// effort), so nobody is stuck behind a sensor that never settles.
const QUALITY_HOLD_MAX_MS = 7000;
// Feedback settles before it is shown: a quality reading has to hold
// this long before the displayed cue, the ready state or the frame
// color follow it. Brief flicker around a threshold never reaches the
// screen; a true blocker (no light at all) shows at once.
const CUE_SETTLE_MS = 500;
// Auto-capture: gates + presence must hold AUTO_ARM_MS, then stay held
// through AUTO_SETTLE_MS, before the shot fires on its own. Nothing is
// counted down on screen (the review asked for one disclosure line,
// not a shutter that turns into a number); a tap at any point captures
// at once. Any lapse cancels silently.
const AUTO_ARM_MS = 2000;
const AUTO_SETTLE_MS = 1500;
// Auto-capture asks more of the frame than the manual unlock does. Day
// one of v2 (n=340 decided): fronts under 300 Laplacian variance verified
// at ~40%, over 600 at 68-70%; glare over 2% failed 25-43%; contrast
// (luma std) under 30 failed 22%. A person tapping tends to wait for a
// cleaner frame; the countdown fired at the minimum and verified worse.
const AUTO_BLUR_MIN = 600;
const AUTO_GLARE_MAX = 0.02;
const AUTO_LUMA_STD_MIN = 30;
const CONTROL_SIZE = 44;
const ID_ASPECT = 1.586;
// The footer / side-panel action slot holds either the shutter group or
// the review's two buttons. One fixed height for both, so the camera
// stage is the same size on the capture and the review screen.
// ── Fit levels ──
// Level 0 is the design. When a translation does not fit (landscape: the
// side panel's content is taller than the panel; portrait: the footer has
// squeezed the camera stage under PORTRAIT_MIN_STAGE_H), the type and
// spacing step down one level at a time (see useFitLevel) until it does,
// or the last level is reached — past that the landscape cue region
// scrolls behind a pinned shutter, and the portrait stage just gets
// smaller, as it did before. Every size on the screen that scales with
// the copy is read from here so the two layouts step down consistently.
const LANDSCAPE_FIT = [
  { heading: 17, headline: 15, detail: 12.5, shutter: 72, label: 13, subline: 11.5, shutterGap: 6, padY: 16, padX: 20, gap: 12, slotMinH: 134, reserveSubline: true },
  { heading: 15, headline: 14, detail: 12, shutter: 64, label: 12.5, subline: 11, shutterGap: 5, padY: 12, padX: 16, gap: 8, slotMinH: 0, reserveSubline: true },
  { heading: 14, headline: 13, detail: 11.5, shutter: 56, label: 12, subline: 10.5, shutterGap: 4, padY: 10, padX: 14, gap: 6, slotMinH: 0, reserveSubline: false },
];
const PORTRAIT_FIT = [
  { heading: 17, headline: 17, detail: 14, detailLines: 2, shutter: 76, label: 14, subline: 12.5, shutterGap: 8, pad: "14px 20px 20px", padV: 34, gap: 14, slotMinH: 150 },
  { heading: 16, headline: 16, detail: 13, detailLines: 2, shutter: 68, label: 13.5, subline: 12, shutterGap: 7, pad: "10px 16px 16px", padV: 26, gap: 10, slotMinH: 138 },
  { heading: 15, headline: 15, detail: 12.5, detailLines: 2, shutter: 60, label: 13, subline: 11.5, shutterGap: 6, pad: "8px 14px 12px", padV: 20, gap: 8, slotMinH: 124 },
];
// Portrait: the footer is content-sized and the stage takes what is
// left, so a long translation shrinks the guide frame. Below this stage
// height the footer steps down a fit level instead; and the stage does
// not go under the floor — past that the footer's cue region scrolls
// (never under a headline and two lines) behind the shutter slot, which
// keeps its height, so on the smallest phones the camera, the whole text
// (by scrolling) and the shutter all stay usable. The floor itself gives
// way on a viewport too short for the floor plus the header and the
// footer's minimum, so the shutter is the last thing to leave the screen.
const PORTRAIT_MIN_STAGE_H = 250;
const PORTRAIT_STAGE_FLOOR = 180;
const PORTRAIT_STAGE_FLOOR_MIN = 120;
// Header plus the footer's minimum at the smallest fit level, roughly.
const PORTRAIT_CHROME_H = 280;

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes docCaptureFadeIn {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes docCaptureFlash {
      0%   { opacity: 0.85; }
      100% { opacity: 0; }
    }

    /* Wordless turn-over hint: the card flips front → back, holds, and
       flips again. Runs over the dimmed feed while we wait for the user
       to physically turn their ID. */
    @keyframes docTurnFlip {
      0%   { transform: rotateY(0deg); }
      35%  { transform: rotateY(180deg); }
      50%  { transform: rotateY(180deg); }
      85%  { transform: rotateY(360deg); }
      100% { transform: rotateY(360deg); }
    }

    @keyframes docCaptureSheetIn {
      from { opacity: 0; transform: translateY(16px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* Shutter busy ring while the photo is being saved. */
    @keyframes docCaptureSpin {
      to { transform: rotate(360deg); }
    }

    /* Hide native video play button / controls on mobile */
    video::-webkit-media-controls,
    video::-webkit-media-controls-panel,
    video::-webkit-media-controls-play-button,
    video::-webkit-media-controls-start-playback-button {
      display: none !important;
      -webkit-appearance: none !important;
    }
  `;
  document.head.appendChild(style);
}

function removeStyles() {
  const el = document.getElementById(STYLE_ID);
  if (el) el.remove();
}

// Analyze a video element for passport indicators (MRZ + aspect ratio).
// Uses the provided canvas ref to avoid creating a new canvas each call.
function analyzePassportFromVideo(video, canvasRef) {
  try {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    const aspectRatio = vw / vh;
    const aspectRatioPassport = aspectRatio >= 1.3 && aspectRatio <= 1.5;

    let canvas = canvasRef.current;
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvasRef.current = canvas;
    }
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, vw, vh);

    // Scan bottom 30% for MRZ bands
    const mrzTop = Math.floor(vh * 0.7);
    const mrzHeight = vh - mrzTop;
    const imageData = ctx.getImageData(0, mrzTop, vw, mrzHeight);
    const pixels = imageData.data;

    const darkThreshold = 80;
    const minDarkPercent = 0.35;
    let denseRowCount = 0;
    let bandCount = 0;
    let inBand = false;

    for (let row = 0; row < mrzHeight; row++) {
      let darkPixels = 0;
      for (let col = 0; col < vw; col++) {
        const idx = (row * vw + col) * 4;
        const luma = pixels[idx] * 0.299 + pixels[idx + 1] * 0.587 + pixels[idx + 2] * 0.114;
        if (luma < darkThreshold) darkPixels++;
      }
      if (darkPixels / vw >= minDarkPercent) {
        if (!inBand) { bandCount++; inBand = true; }
        denseRowCount++;
      } else {
        inBand = false;
      }
    }

    const mrzDetected = bandCount >= 2 && denseRowCount >= 10;

    return {
      isPassport: mrzDetected || aspectRatioPassport,
      mrzDetected,
      aspectRatioPassport,
      aspectRatio: aspectRatio.toFixed(3),
      bandCount,
      denseRowCount,
    };
  } catch {
    return null;
  }
}

/**
 * Map a rectangle drawn over an `object-fit: cover` video back to source
 * pixel coordinates, so the crop matches what the user saw inside the
 * guide frame regardless of orientation or how the feed was letterboxed.
 */
function guideRectToVideoCrop(video, videoRect, guideRect, padRatio = 0.15) {
  const vWidth = video.videoWidth;
  const vHeight = video.videoHeight;
  if (!vWidth || !vHeight || !videoRect.width || !videoRect.height) return null;

  const scale = Math.max(videoRect.width / vWidth, videoRect.height / vHeight);
  const displayedW = vWidth * scale;
  const displayedH = vHeight * scale;
  // object-fit: cover centers the overflow, so the visible region starts
  // at a (possibly negative) offset inside the element box.
  const offsetX = (videoRect.width - displayedW) / 2;
  const offsetY = (videoRect.height - displayedH) / 2;

  const padX = guideRect.width * padRatio;
  const padY = guideRect.height * padRatio;

  const localX = guideRect.left - videoRect.left - offsetX - padX;
  const localY = guideRect.top - videoRect.top - offsetY - padY;
  const localW = guideRect.width + padX * 2;
  const localH = guideRect.height + padY * 2;

  const cropX = Math.round(localX / scale);
  const cropY = Math.round(localY / scale);
  const cropW = Math.round(localW / scale);
  const cropH = Math.round(localH / scale);

  const x = Math.max(0, Math.min(cropX, vWidth));
  const y = Math.max(0, Math.min(cropY, vHeight));
  const w = Math.min(cropW - (x - cropX), vWidth - x);
  const h = Math.min(cropH - (y - cropY), vHeight - y);
  if (w <= 0 || h <= 0) return null;
  return { x, y, w, h };
}

/**
 * Measure an element's box. Callback-ref based so it keeps working when
 * the measured element is swapped for another one (the camera stage is a
 * different node in the portrait and landscape layouts).
 */
function useMeasuredSize() {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const observerRef = useRef(null);
  const ref = useCallback((el) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setSize((prev) =>
        prev.width === r.width && prev.height === r.height ? prev : { width: r.width, height: r.height }
      );
    };
    update();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      observerRef.current = ro;
    }
  }, []);
  useEffect(() => () => observerRef.current?.disconnect(), []);
  return { ref, width: size.width, height: size.height };
}

// Viewport size, for the fit logic's reset: a rotation or a browser bar
// collapsing starts the fit over from level 0; anything the fit itself
// changes (the stage height, the panel's content) must not.
function useViewportSize() {
  const read = () => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  });
  const [size, setSize] = useState(read);
  useEffect(() => {
    const onResize = () => {
      const next = read();
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return size;
}

/**
 * Fit-to-space for translated copy. `overflows()` is read after every
 * commit that changed the copy; while it reports true and a smaller
 * level exists, the level steps down by one (and the screen re-renders
 * at the smaller type). It only ever steps down for a given `resetToken`
 * — a longer cue arriving mid-session settles at a smaller size and
 * stays there, rather than the type pumping up and down with each cue —
 * and starts over at 0 when the token changes (rotation, resize, locale).
 */
function useFitLevel({ maxLevel, overflows, resetToken, contentKey }) {
  const [level, setLevel] = useState(0);
  const tokenRef = useRef(resetToken);
  // overflows reads the DOM and is a fresh closure every render; kept in
  // a ref so the effect runs only when the level, the token or the copy
  // changes.
  const overflowsRef = useRef(overflows);
  overflowsRef.current = overflows;
  useLayoutEffect(() => {
    if (tokenRef.current !== resetToken) {
      tokenRef.current = resetToken;
      if (level !== 0) {
        setLevel(0);
        return;
      }
    }
    if (level < maxLevel && overflowsRef.current()) setLevel(level + 1);
  }, [level, maxLevel, resetToken, contentKey]);
  return level;
}

// A column that scrolls its content when there is more than fits, and
// says so: a fade over whichever edge has more text behind it. With
// `center` the content sits in the middle while there is room (auto
// margins collapse to 0 once the content is taller than the box, so it
// is then top-aligned and scrolls from its first line). Scrolls back to
// the top whenever the content changes (`contentKey`), so a new cue is
// always read from its headline.
function ScrollRegion({ children, region, center = false, contentKey, style }) {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const more = el.scrollHeight - el.clientHeight > 1;
    const top = more && el.scrollTop > 1;
    const bottom = more && el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = 0;
    update();
  }, [contentKey, update]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener("scroll", update, { passive: true });
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener("scroll", update);
      if (ro) ro.disconnect();
    };
  }, [update]);
  const fade = (side) => (
    <div
      key={side}
      aria-hidden="true"
      data-capture-region={`scroll-fade-${side}`}
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        [side]: 0,
        height: 26,
        pointerEvents: "none",
        background: `linear-gradient(to ${side === "top" ? "bottom" : "top"}, ${BG} 15%, rgba(14,15,16,0))`,
      }}
    />
  );
  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", minHeight: 0, ...style }}>
      <div
        ref={ref}
        data-capture-region={region}
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div style={{ margin: center ? "auto 0" : 0, width: "100%", flexShrink: 0 }}>{children}</div>
      </div>
      {edges.top && fade("top")}
      {edges.bottom && fade("bottom")}
    </div>
  );
}

// ── Presentational pieces ──

function RoundControl({ label, onClick, children, style, translucent = false, region }) {
  return (
    <button
      type="button"
      aria-label={label}
      data-capture-region={region}
      onClick={onClick}
      style={{
        width: CONTROL_SIZE,
        height: CONTROL_SIZE,
        borderRadius: CONTROL_SIZE / 2,
        border: "none",
        background: translucent ? "rgba(14,15,16,0.6)" : "rgba(255,255,255,0.08)",
        color: FG,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// `pinned`: in the landscape panel (a column) the heading keeps its
// height whatever the cue needs; in the portrait header (a row) it
// shrinks and wraps so the close and help buttons stay on screen.
function StepHeading({ title, stepText, totalSteps, isBack, accent, readyColor = GREEN, size = 17, pinned = false }) {
  return (
    <div data-capture-region="heading" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 0, maxWidth: "100%", flexShrink: pinned ? 0 : 1 }}>
      <span style={{ fontSize: size, fontWeight: 600, color: FG, lineHeight: 1.2, textAlign: "center", maxWidth: "100%", ...WRAP_TEXT }}>
        {title}
      </span>
      {totalSteps > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 22,
              height: 4,
              borderRadius: 2,
              background: isBack ? readyColor : accent,
              transition: "background 300ms",
            }}
          />
          <div
            style={{
              width: 22,
              height: 4,
              borderRadius: 2,
              background: isBack ? accent : "rgba(255,255,255,0.2)",
              transition: "background 300ms",
            }}
          />
          <span style={{ fontFamily: MONO, fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>{stepText}</span>
        </div>
      )}
    </div>
  );
}

export function CornerBrackets({ color, size = 30, radius = 12, stroke = 3 }) {
  const base = {
    position: "absolute",
    width: size,
    height: size,
    transition: "border-color 300ms",
    pointerEvents: "none",
  };
  const edge = `${stroke}px solid ${color}`;
  return (
    <>
      <div style={{ ...base, left: 0, top: 0, borderLeft: edge, borderTop: edge, borderTopLeftRadius: radius }} />
      <div style={{ ...base, right: 0, top: 0, borderRight: edge, borderTop: edge, borderTopRightRadius: radius }} />
      <div style={{ ...base, left: 0, bottom: 0, borderLeft: edge, borderBottom: edge, borderBottomLeftRadius: radius }} />
      <div style={{ ...base, right: 0, bottom: 0, borderRight: edge, borderBottom: edge, borderBottomRightRadius: radius }} />
    </>
  );
}

// The schematic ID the intro stage shows inside the guide brackets: the
// turn-over hint's front face (same container, same content), at any
// width. Aspect is the hint's 180 × 114.
export function IdCardIllustration({ accent, width = 260 }) {
  const k = width / 180;
  const height = Math.round(114 * k);
  return (
    <div
      aria-hidden="true"
      style={{
        width,
        height,
        boxSizing: "border-box",
        padding: Math.round(13 * k),
        borderRadius: Math.round(13 * k),
        background: FG,
        border: `2px solid ${accent}`,
        display: "flex",
        flexDirection: "column",
        gap: Math.round(9 * k),
        flexShrink: 0,
      }}
    >
      <IdCardFrontContent width={width} />
    </div>
  );
}

// The one guidance block. A headline says whether a photo can be taken
// ("You can take a photo" / "Ready to take photo" / a blocker) and one
// detail line carries the single most useful correction. It is always
// rendered, at the same size, in the same place — so a change of cue
// never moves the camera, the guide frame or the shutter.
// Sizes come from the fit level (LANDSCAPE_FIT / PORTRAIT_FIT). The
// portrait footer reserves `detailLines` lines for the detail so the
// stage above it keeps its height as the cue changes; the landscape
// panel pins the heading and the shutter and lets the cue take the
// middle, so it reserves nothing there (detailLines 0) — a reserved
// height would only make a long translation overflow sooner.
function CueBlock({ headline, detail, tone, compact, headlineSize = 17, detailSize = 14, detailLines = 2, readyColor = GREEN, warnColor = AMBER }) {
  const headlineColor = tone === "ready" ? readyColor : tone === "blocked" ? warnColor : FG;
  // Glyph on the status chips: the original near-black on the stock
  // green/amber, otherwise whichever of white / near-black reads on the
  // configured chip color.
  const readyGlyph = readyColor === GREEN ? "#0B0F10" : contrastingForeground(readyColor);
  const warnGlyph = warnColor === AMBER ? "#0B0F10" : contrastingForeground(warnColor);
  return (
    <div
      role="status"
      aria-live="polite"
      data-capture-region="cue"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: compact ? 4 : 6,
        width: "100%",
        maxWidth: compact ? "100%" : 340,
        // The block sits in ScrollRegion's full-width wrapper, not a
        // centering flex column, so the auto margins are what center it
        // once the footer is wider than maxWidth (phones over 380px).
        margin: "0 auto",
        boxSizing: "border-box",
        padding: compact ? 0 : "0 8px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          minHeight: Math.round(headlineSize * 1.25),
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        {tone === "ready" && (
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: 10,
              background: readyColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconCheck size={12} stroke={3.2} color={readyGlyph} aria-hidden="true" />
          </div>
        )}
        {tone === "blocked" && (
          <div
            aria-hidden="true"
            style={{
              width: 20,
              height: 20,
              borderRadius: 10,
              background: warnColor,
              color: warnGlyph,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: 13,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            !
          </div>
        )}
        <span
          style={{
            fontSize: headlineSize,
            fontWeight: 600,
            lineHeight: 1.25,
            color: headlineColor,
            textAlign: "center",
            transition: "color 200ms ease",
            minWidth: 0,
            ...WRAP_TEXT,
          }}
        >
          {headline}
        </span>
      </div>
      <span
        style={{
          display: "block",
          width: "100%",
          fontSize: detailSize,
          lineHeight: 1.4,
          minHeight: detailLines > 0 ? Math.round(detailSize * 1.4 * detailLines) : undefined,
          color: GUIDE_TEXT,
          textAlign: "center",
          ...WRAP_BODY,
        }}
      >
        {detail}
      </span>
    </div>
  );
}

// The shutter never changes what it means: one button, tap to take the
// photo. While it is held (camera starting, gates not yet passed, no
// light) the inner disc is hollow — a visibly "not yet" state rather
// than a faded copy of the live button — and the line under it says
// what unlocks it. While a shot is saving (busy) it shows a spinning ring
// and takes no taps, so a double tap cannot fire a second capture.
function Shutter({ onClick, disabled, size = 80, label, busy = false, busyLabel = "Saving your photo" }) {
  const inner = size - 20;
  return (
    <button
      type="button"
      aria-label={busy ? busyLabel : label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        border: `4px solid ${FG}`,
        padding: 0,
        background: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled || busy ? "default" : "pointer",
        opacity: busy ? 1 : disabled ? 0.55 : 1,
        transition: "opacity 200ms",
        flexShrink: 0,
      }}
    >
      {busy ? (
        <div
          style={{
            width: inner - 8,
            height: inner - 8,
            borderRadius: "50%",
            border: `4px solid rgba(244,245,242,0.25)`,
            borderTopColor: FG,
            animation: "docCaptureSpin 0.9s linear infinite",
          }}
        />
      ) : (
        <div
          style={{
            width: inner,
            height: inner,
            borderRadius: inner / 2,
            boxSizing: "border-box",
            background: disabled ? "transparent" : FG,
            border: disabled ? "2px dashed rgba(244,245,242,0.7)" : "2px solid transparent",
            transition: "background 200ms, border-color 200ms",
          }}
        />
      )}
    </button>
  );
}

// Shutter with its caption and the one-time auto-capture disclosure.
// Every line is always rendered so the group's height, and with it the
// camera stage above it, never changes while the user is framing.
// The group is as wide as its slot (never wider): it used to size itself
// to its longest caption, and with flex-shrink 0 a one-line German
// disclosure ran off both sides of the landscape panel. Captions wrap.
function ShutterGroup({
  onClick, disabled, size, label, subline, sublineTone = "muted", compact, busy = false, busyLabel,
  labelSize = 14, sublineSize = 12.5, gap = 8, reserveSubline = true, warnColor = AMBER,
}) {
  return (
    <div
      data-capture-region="shutter-group"
      style={{ width: "100%", minWidth: 0, boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", gap, flexShrink: 0 }}
    >
      <Shutter onClick={onClick} disabled={disabled} size={size} label={label} busy={busy} busyLabel={busyLabel} />
      <span
        style={{
          fontSize: labelSize,
          fontWeight: 600,
          lineHeight: 1.25,
          color: FG,
          opacity: disabled ? 0.55 : 1,
          transition: "opacity 200ms",
          textAlign: "center",
          maxWidth: "100%",
          ...WRAP_TEXT,
        }}
      >
        {label}
      </span>
      {/* Always rendered (two lines reserved while there is room) so the
          slot does not change height between the held hint and the auto
          disclosure. */}
      <span
        style={{
          width: "100%",
          maxWidth: compact ? "100%" : 300,
          boxSizing: "border-box",
          fontSize: sublineSize,
          lineHeight: 1.3,
          minHeight: reserveSubline ? Math.round(sublineSize * 1.3 * 2) : undefined,
          color: sublineTone === "warn" ? warnColor : MUTED,
          textAlign: "center",
          transition: "color 200ms",
          ...WRAP_BODY,
        }}
      >
        {subline}
      </span>
    </div>
  );
}

// The front face of the schematic ID: a header bar, a portrait block and
// four text lines, drawn at the proportions of the turn-over hint's
// 180px card and scaled from there. Shared by the turn-over hint and the
// intro stage so the document looks the same everywhere it appears.
function IdCardFrontContent({ width = 180 }) {
  const k = width / 180;
  const px = (n) => Math.max(1, Math.round(n * k));
  return (
    <>
      <div style={{ height: px(10), width: "55%", borderRadius: px(3), background: "#6B7480" }} />
      <div style={{ flexGrow: 1, display: "flex", gap: px(13) }}>
        <div style={{ width: px(43), borderRadius: px(4), background: "#9AA3AD" }} />
        <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: px(8) }}>
          <div style={{ height: px(7), width: "90%", borderRadius: px(2), background: "#B8BEC6" }} />
          <div style={{ height: px(7), width: "70%", borderRadius: px(2), background: "#B8BEC6" }} />
          <div style={{ height: px(7), width: "80%", borderRadius: px(2), background: "#B8BEC6" }} />
          <div style={{ height: px(7), width: "55%", borderRadius: px(2), background: "#B8BEC6" }} />
        </div>
      </div>
    </>
  );
}

// Wordless front → back hint drawn over the dimmed feed. No copy, so it
// works in every language; the header already says "Back of ID".
function TurnOverlay({ accent, compact }) {
  const w = compact ? 170 : 180;
  const h = compact ? 108 : 114;
  const face = {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    padding: 13,
    borderRadius: 13,
    background: FG,
    border: `2px solid ${accent}`,
    display: "flex",
    flexDirection: "column",
    gap: 9,
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
  };
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        background: "rgba(8,8,9,0.86)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
        <div style={{ perspective: 700 }}>
          <div
            style={{
              position: "relative",
              width: w,
              height: h,
              transformStyle: "preserve-3d",
              animation: "docTurnFlip 3s cubic-bezier(0.45, 0.05, 0.2, 1) infinite",
            }}
          >
            <div style={face}>
              <IdCardFrontContent width={w} />
            </div>
            <div style={{ ...face, transform: "rotateY(180deg)" }}>
              <div
                style={{
                  flexGrow: 1,
                  borderRadius: 3,
                  background:
                    "repeating-linear-gradient(0deg, transparent 0 7px, #F4F5F2 7px 9px), repeating-linear-gradient(90deg, #2A2F36 0 3px, transparent 3px 5px, #2A2F36 5px 6px, transparent 6px 10px, #2A2F36 10px 13px, transparent 13px 15px)",
                }}
              />
              <div
                style={{
                  height: 18,
                  width: "60%",
                  alignSelf: "flex-end",
                  background:
                    "repeating-linear-gradient(90deg, #2A2F36 0 2px, transparent 2px 4px, #2A2F36 4px 5px, transparent 5px 7px)",
                }}
              />
            </div>
          </div>
        </div>
        <svg width={w} height="34" viewBox="0 0 200 34" aria-hidden="true" preserveAspectRatio="none">
          <path d="M28 6 Q100 40 172 6" fill="none" stroke={accent} strokeWidth="3" strokeLinecap="round" />
          <path d="M158 4 L173 5 L168 19" fill="none" stroke={accent} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

// `compact` (a viewport under HELP_SHEET_COMPACT_H tall, i.e. a phone
// sideways with the browser bar up) tightens the sheet's spacing so the
// three tips and the button fit without scrolling.
const HELP_SHEET_COMPACT_H = 360;
function HelpSheet({ locale, accent, buttonColor, accentText, onClose, compact = false }) {
  const tips = [
    { Icon: IconSunHigh, text: locale?.setup?.wellLit || "You are in a well-lit area" },
    { Icon: IconId, text: locale?.setup?.flatSurface || "Place ID on a flat, dark surface" },
    { Icon: IconCapture, text: locale?.setup?.allCorners || "All four corners must be visible" },
  ];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={locale?.help?.title || "Tips for a clear photo"}
      data-capture-region="help-sheet"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 60,
        background: "rgba(8,8,9,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: compact ? 12 : 20,
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 380,
          maxHeight: "100%",
          overflowY: "auto",
          boxSizing: "border-box",
          padding: compact ? "14px 16px 14px" : "22px 22px 20px",
          borderRadius: 18,
          background: "#16181A",
          border: "1px solid rgba(255,255,255,0.12)",
          color: FG,
          display: "flex",
          flexDirection: "column",
          gap: compact ? 12 : 18,
          animation: "docCaptureSheetIn 240ms ease-out",
        }}
      >
        <span style={{ fontSize: compact ? 17 : 19, fontWeight: 600, lineHeight: 1.25, ...WRAP_TEXT }}>
          {locale?.help?.title || "Tips for a clear photo"}
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: compact ? 10 : 14 }}>
          {tips.map(({ Icon, text }, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: compact ? 12 : 14 }}>
              <div
                style={{
                  width: compact ? 34 : 38,
                  height: compact ? 34 : 38,
                  borderRadius: 19,
                  background: "rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: accent,
                }}
              >
                <Icon size={22} stroke={1.6} aria-hidden="true" />
              </div>
              <span style={{ fontSize: compact ? 14 : 15, lineHeight: 1.4, color: GUIDE_TEXT, minWidth: 0, ...WRAP_BODY }}>{text}</span>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            minHeight: compact ? 44 : 48,
            padding: compact ? "10px 20px" : "12px 20px",
            borderRadius: 24,
            border: "none",
            background: buttonColor || accent,
            flexShrink: 0,
            color: accentText,
            fontSize: 16,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
            ...WRAP_TEXT,
          }}
        >
          {locale?.help?.done || "Got it"}
        </button>
      </div>
    </div>
  );
}

function PrimaryButton({ children, onClick, accent, accentText, compact }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        minHeight: compact ? 46 : 52,
        padding: "12px 20px",
        borderRadius: compact ? 23 : 26,
        border: "none",
        background: accent,
        color: accentText,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.25,
        cursor: "pointer",
        fontFamily: "inherit",
        ...WRAP_TEXT,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, compact }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        minHeight: compact ? 46 : 52,
        padding: "12px 20px",
        borderRadius: compact ? 23 : 26,
        border: "1.5px solid rgba(244,245,242,0.4)",
        background: "transparent",
        color: FG,
        fontSize: 16,
        fontWeight: 600,
        lineHeight: 1.25,
        cursor: "pointer",
        fontFamily: "inherit",
        ...WRAP_TEXT,
      }}
    >
      {children}
    </button>
  );
}

export default function DocumentCapture({
  onCapture,
  onCaptureStarted,
  onError,
  onReady,
  onCameraAllowed,
  onClose,
  side = "front",
  totalSteps = 2,
  captureSettings = {},
  paused = false,
  transitioning = false,
  reviewImage = null,
  reviewSide = null,
  // Two-sided flow: both captures reviewed together at the end.
  // { front, back } preview URLs; onReviewRetakeSide("front"|"back").
  reviewImages = null,
  onReviewRetakeSide,
  onReviewAccept,
  onReviewRetake,
  isBrowserHandoffSession = false,
  preAcquiredStream = null,
  requestId = null,
  onPhotoFallback,
}) {
  const theme = useMantineTheme();
  const { data, settings } = useContext(DataContext) || {};
  const locale = useLocale("document-capture");
  const isLandscape = useIsLandscape();
  // This screen lays out sideways on purpose — keep the app-wide "rotate
  // your device" prompt out of the way while we're mounted.
  useDeclareLandscapeSupport(true);
  // Stamped onto every camera/capture telemetry event so provider,
  // capture mode and the capture UI arm are queryable on the whole
  // funnel, not just uploads. Orientation is read at emit time (it
  // changes while we're mounted), so every event from this screen says
  // which way the phone was held.
  // capture_build: which v2 build emitted the event, so the A/B read can
  // cut by build instead of by deploy time. "v2d" = the Sept 2026 redesign
  // (one cue, advisory gates, no countdown, combined two-sided review).
  // Bump it when the v2 screen changes in a way the funnel should split on.
  const telemetryBase = { provider: "veratad", mode: resolveCaptureMode(captureSettings), capture_ui: "v2", capture_build: CAPTURE_BUILD_V2 };
  const emit = (name, props = {}, opts) =>
    trackEvent(name, { ...telemetryBase, orientation: currentOrientation(), ...props }, opts);
  const webcamRef = useRef(null);
  const videoElRef = useRef(null); // plain <video> ref when using pre-acquired stream
  const guideRef = useRef(null);
  const passportCanvasRef = useRef(null);
  const captureCanvasRef = useRef(null);
  // Re-entrancy guard for handleCapture. On Android the quality-analysis
  // frame loop saturates the main thread, so the canvas.toBlob completion
  // task can wait many seconds; meanwhile input events still get through,
  // the user sees nothing happen and taps again, and every tap queued a
  // full capture that later landed in a burst (30% of Android v2 sessions
  // logged bursts of id_doc_captured rows with identical metrics). Set on
  // entry to the tap timestamp; while set, the frame loop is suspended so
  // the encode can finish, extra taps are dropped (id_doc_capture_deduped
  // with the gap), and a 10s safety timer releases the guard so a stuck
  // capture can be retried. Otherwise released when the capture fails to
  // produce a blob, or by the effect below once the review closes / the
  // side changes.
  const captureInFlightRef = useRef(null);
  const captureTimeoutRef = useRef(null);
  const captureGenRef = useRef(0);
  const CAPTURE_TIMEOUT_MS = 10000;
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [androidOsBlock, setAndroidOsBlock] = useState(false);
  const [flashing, setFlashing] = useState(false);
  // True from the shutter tap until the photo is handed to the parent (or
  // the capture is released): disables the shutter and shows a busy ring
  // so the wait for the encode reads as progress, not as a missed tap.
  const [capturing, setCapturing] = useState(false);
  const cameraRespondedRef = useRef(false);
  const retryAttemptedRef = useRef(false);
  const flashTimerRef = useRef(null);

  // The camera stage is the region the guide frame is laid out in — the
  // whole viewport minus the header/footer (portrait) or side panel
  // (landscape). Measured, not computed, so the frame tracks rotation,
  // safe-area insets and the browser chrome collapsing.
  const { ref: stageRef, width: stageW, height: stageH } = useMeasuredSize();

  // When using a pre-acquired stream, we render a plain <video> instead of
  // react-webcam (which always calls getUserMedia internally and can't accept
  // an existing stream). We shim webcamRef so the rest of the code that uses
  // webcamRef.current.video and webcamRef.current.getScreenshot() keeps working.
  useEffect(() => {
    if (!preAcquiredStream) return;

    const attach = () => {
      const video = videoElRef.current;
      if (!video) {
        requestAnimationFrame(attach);
        return;
      }
      console.log("[Capture] Attaching pre-acquired stream to plain <video>");
      // The parent already ran getUserMedia successfully to get this stream,
      // so this is the "granted" signal on the pre-acquired path.
      emit("id_doc_camera_permission_granted", {}, { once: true });
      video.srcObject = preAcquiredStream;
      video.play().catch(() => {});

      // Shim webcamRef so existing code (handleCapture, detection, etc.) works
      webcamRef.current = {
        video,
        getScreenshot: () => {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext("2d").drawImage(video, 0, 0);
          return canvas.toDataURL("image/jpeg", ID_DOC_JPEG_QUALITY);
        },
      };

      cameraRespondedRef.current = true;
      if (onCameraAllowed) onCameraAllowed();
      const checkReady = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
          emit("id_doc_camera_ready", { width: video.videoWidth, height: video.videoHeight, side });
          setCameraReady(true);
        } else {
          requestAnimationFrame(checkReady);
        }
      };
      requestAnimationFrame(checkReady);
    };
    requestAnimationFrame(attach);

    return () => {
      preAcquiredStream.getTracks().forEach((t) => t.stop());
    };
  }, [preAcquiredStream]);
  const [passportInfo, setPassportInfo] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [manualUnlocked, setManualUnlocked] = useState(false);

  const primaryColor =
    theme.colors[theme.primaryColor]?.[5] || theme.colors.blue[5];
  // Label color for anything drawn ON the accent (the "Use this photo"
  // CTA, the help sheet's button) — the publisher's button text color
  // when set, else a readable fallback derived from the accent.
  // Per-surface overrides (styles.components), each falling back to the
  // primary so unconfigured sessions are unchanged:
  //   docCapture.glowColor     frame corner brackets while a document is
  //                            detected (green "ready" state is untouched)
  //   button.backgroundColor   filled CTAs ("Use this photo", help "Got it")
  // v2 has no "Captured by" branding row, so captureBranding.show has
  // nothing to hide here.
  const componentStyles = data?.styles?.components || {};
  const glowColor =
    normalizeColor(componentStyles.docCapture?.glowColor) || primaryColor;
  const ctaColor =
    normalizeColor(componentStyles.button?.backgroundColor) || primaryColor;
  //   docCapture.stepColor         "Step 1 of 2" progress bars in the heading
  //   docCapture.guidanceIconColor tip icons in the help sheet
  //   docCapture.turnHintColor     flip-your-card animation border and arrow
  const stepColor =
    normalizeColor(componentStyles.docCapture?.stepColor) || primaryColor;
  const guidanceIconColor =
    normalizeColor(componentStyles.docCapture?.guidanceIconColor) || primaryColor;
  const turnHintColor =
    normalizeColor(componentStyles.docCapture?.turnHintColor) || primaryColor;
  //   checkmark.color          the green "ready" state: brackets, status
  //                            chip, completed step bar
  //   docCapture.warningColor  the amber "blocked" chip and held-shutter hint
  const readyColor = resolveCheckmarkColor(componentStyles) || GREEN;
  const warnColor =
    normalizeColor(componentStyles.docCapture?.warningColor) || AMBER;
  const onAccent = resolveOnAccentColor({
    buttonTextColor: data?.styles?.components?.button?.color,
    accent: ctaColor,
  });

  // Check permissions API if available — for handoff sessions, detect if
  // camera is already blocked so we can show unblock instructions instead
  // of trying and silently failing.
  useEffect(() => {
    console.log("[Capture] Component MOUNTED, starting camera flow");
    emit("id_doc_camera_requested", {}, { once: true });
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "camera" })
        .then((status) => {
          console.log("[Capture] Permission API state:", status.state);
          const showAndroidBlock = shouldShowAndroidCameraBlock();
          if (status.state === "denied" && (isBrowserHandoffSession || showAndroidBlock)) {
            console.warn("[Capture] Camera pre-denied — showing unblock instructions", showAndroidBlock ? "(Android OS block likely)" : "");
            cameraRespondedRef.current = true;
            setCameraBlocked(true);
            if (showAndroidBlock) setAndroidOsBlock(true);
            removeStyles();

            window.dispatchEvent(new CustomEvent("veratad-camera-error"));

            if (onErrorRef.current) {
              onErrorRef.current({
                type: "camera_error",
                name: "NotAllowedError",
                message: "Permission denied",
              });
            }
            if (typeof window.__veratadIdDocCameraError === "function") {
              window.__veratadIdDocCameraError();
            }
          }
          status.onchange = () => {
            console.log("[Capture] Permission state CHANGED to:", status.state);
          };
        })
        .catch((err) => {
          console.log("[Capture] Permission API not supported:", err.message);
        });
    }
  }, []);

  // Timeout: if getUserMedia neither resolves nor rejects (e.g. iOS Safari
  // "Cancel" dismisses the prompt without firing either callback), treat it
  // as a camera error so the UI doesn't hang indefinitely.
  // Use a longer timeout for browser handoff sessions — the user is switching
  // apps and the permission prompt may take longer to appear.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const cameraTimeoutMs = isBrowserHandoffSession ? 15000 : 7000;

  useEffect(() => {
    console.log(`[Capture] Timeout effect registered (${cameraTimeoutMs / 1000}s)`);
    const timer = setTimeout(() => {
      console.log(`[Capture] ${cameraTimeoutMs / 1000}s timeout fired — cameraResponded:`, cameraRespondedRef.current, "cameraReady:", cameraReady);
      if (!cameraRespondedRef.current) {
        console.warn("[Capture] Camera permission timeout — no response after timeout");
        emit("id_doc_camera_permission_timeout", {});

        setCameraFailed(true);
        removeStyles();

        window.dispatchEvent(new CustomEvent("veratad-camera-error"));

        if (onErrorRef.current) {
          onErrorRef.current({
            type: "camera_error",
            name: "TimeoutError",
            message: "Camera permission prompt timed out",
          });
        }
        if (typeof window.__veratadIdDocCameraError === "function") {
          window.__veratadIdDocCameraError();
        }
      }
    }, cameraTimeoutMs);
    return () => clearTimeout(timer);
  }, []);

  // Lifecycle logging + cleanup
  useEffect(() => {
    console.log("[Capture] MOUNT side:", side);
    return () => {
      console.log("[Capture] UNMOUNT side:", side);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      // Free reusable canvases
      if (passportCanvasRef.current) {
        passportCanvasRef.current.width = 0;
        passportCanvasRef.current.height = 0;
        passportCanvasRef.current = null;
      }
      if (captureCanvasRef.current) {
        captureCanvasRef.current.width = 0;
        captureCanvasRef.current.height = 0;
        captureCanvasRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    console.log("[Capture] Props changed — side:", side, "paused:", paused, "cameraReady:", cameraReady);
  }, [side, paused, cameraReady]);

  // Close the help sheet whenever the flow moves on (side change, review).
  useEffect(() => {
    setHelpOpen(false);
  }, [side, reviewImage, reviewImages]);

  // Orientation funnel: which way the screen was first shown, and every
  // rotation while it's up (with the side and whether a review was open,
  // so a rotate-to-fit habit is visible in the data).
  const sideRef = useRef(side);
  sideRef.current = side;
  const reviewOpenRef = useRef(!!reviewImage || !!reviewImages);
  reviewOpenRef.current = !!reviewImage || !!reviewImages;
  const orientationRef = useRef(isLandscape ? "landscape" : "portrait");
  useEffect(() => {
    emit("id_doc_capture_ui_shown", { side: sideRef.current }, { once: true });
  }, []);
  useEffect(() => {
    const next = isLandscape ? "landscape" : "portrait";
    if (next === orientationRef.current) return;
    emit("id_doc_orientation_changed", {
      from: orientationRef.current,
      to: next,
      side: sideRef.current,
      in_review: reviewOpenRef.current,
    });
    orientationRef.current = next;
  }, [isLandscape]);

  // The rotation moves the stage's <video> to a new spot in the DOM (see
  // the keyed layouts below). Safari can pause a moved video, so ask it
  // to keep playing; a no-op when it never stopped.
  useEffect(() => {
    const video = videoElRef.current || webcamRef.current?.video;
    if (!video || !video.paused) return;
    const p = video.play?.();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [isLandscape]);

  const {
    modelsLoaded,
    modelLoadError,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    documentType,
    confidence,
    showManualCapture,
    setOnAutoCapture,
    setGuideBoxProvider,
    getBestFrame,
    measureCurrentSharpness,
    getLastFrameMetrics,
    setFrameLoopSuspended,
    getFrameSeq,
    getThresholds,
    getFrameChain,
    areaRatio,
    captured,
    yoloDetail,
    captureStrategy,
    yoloMissCount,
  } = useDocumentDetection(webcamRef, !paused && cameraReady, captureSettings, side);

  // Manual capture (no auto-capture): "stupid" has no gates at all,
  // "assisted" measures brightness / blur / glare in the guide frame and
  // this screen holds the shutter until they pass.
  const isStupidMode = captureStrategy === "stupid" || captureStrategy === "assisted";
  const isAssisted = captureStrategy === "assisted";
  const feedbackMessage = locale?.feedback?.[feedbackKey] || feedbackKey;

  // Count passing gates for the guide-frame color
  const passingCount = Object.values(gatingState).filter(Boolean).length;

  // Guide frame: ID-card aspect (1.586:1), sized to the stage. Portrait
  // gives it most of the width; landscape is height-bound so the side
  // panel keeps its room. The frame is centered by flex, and the crop
  // reads its real on-screen rect, so nothing here needs to be exact.
  const maxGuideW = stageW * (isLandscape ? 0.8 : 0.88);
  const maxGuideH = stageH * (isLandscape ? 0.76 : 0.56);
  let guideWidth = Math.min(maxGuideW, 480);
  let guideHeight = guideWidth / ID_ASPECT;
  if (guideHeight > maxGuideH) {
    guideHeight = Math.max(maxGuideH, 0);
    guideWidth = guideHeight * ID_ASPECT;
  }
  const guideRadius = isLandscape ? 14 : 12;
  const bracketSize = isLandscape ? 36 : 30;

  // Inject/remove CSS
  useEffect(() => {
    injectStyles();
    return removeStyles;
  }, []);

  // Notify parent when models + camera are ready
  useEffect(() => {
    if (modelsLoaded && cameraReady && onReady) {
      onReady();
    }
  }, [modelsLoaded, cameraReady, onReady]);

  const flash = useCallback(() => {
    setFlashing(true);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashing(false), 450);
  }, []);

  // Tell the frame loop where the guide frame is, in video pixels, so the
  // quality gates measure what the user is framing (not a fixed box).
  useEffect(() => {
    setGuideBoxProvider(() => {
      const video = webcamRef.current?.video;
      const guide = guideRef.current;
      if (!video || !guide) return null;
      const crop = guideRectToVideoCrop(
        video,
        video.getBoundingClientRect(),
        guide.getBoundingClientRect(),
        0
      );
      return crop ? { x: crop.x, y: crop.y, width: crop.w, height: crop.h } : null;
    });
    return () => setGuideBoxProvider(null);
  }, [setGuideBoxProvider]);

  const feedReady = cameraReady && (isStupidMode || modelsLoaded);

  // ── Light quality gates (assisted mode) ──
  // The loop reports blur / glare / lighting per frame (~6/s). Low-end
  // sensors flicker around any threshold, so the ready state follows the
  // gates with a time-based settle: every gate has to pass for
  // CUE_SETTLE_MS before "ready" shows, and fail for as long before it
  // goes again. The same settled state holds the shutter until the gates
  // pass (or the hold cap below releases it), so the button never
  // flickers with a single frame.
  // Gate state is only trusted once a frame has been analysed for THIS
  // side: the hook keeps the last frame's state across the side switch
  // long enough for the previous side's passing gates to read as ready
  // (back-side "ready" fired 2-3s before its loop started).
  const sideStartSeqRef = useRef(0);
  const frameFresh = typeof getFrameSeq !== "function" || getFrameSeq() > sideStartSeqRef.current;
  const gatesPassNow =
    isAssisted && frameFresh && !isTooDark && gatingState.lighting && gatingState.blur && gatingState.steady && gatingState.glare;
  const [qualityOk, setQualityOk] = useState(false);
  // When the current run of passing / failing frames began.
  const qualityRunRef = useRef({ passSince: null, failSince: null });
  const qualityReadyAtRef = useRef(null);
  const qualitySideStartRef = useRef(performance.now());
  useEffect(() => {
    if (!isAssisted) return;
    const now = performance.now();
    const run = qualityRunRef.current;
    if (gatesPassNow) {
      run.failSince = null;
      if (run.passSince == null) run.passSince = now;
      else if (!qualityOk && now - run.passSince >= CUE_SETTLE_MS) setQualityOk(true);
    } else {
      run.passSince = null;
      if (run.failSince == null) run.failSince = now;
      else if (qualityOk && now - run.failSince >= CUE_SETTLE_MS) setQualityOk(false);
    }
    // gatingState is a fresh object every frame, so this runs per frame.
  }, [gatingState, isTooDark, isAssisted]);
  // Reset per side and record time-to-ready once per side.
  useEffect(() => {
    qualityRunRef.current = { passSince: null, failSince: null };
    qualityReadyAtRef.current = null;
    qualitySideStartRef.current = performance.now();
    sideStartSeqRef.current = typeof getFrameSeq === "function" ? getFrameSeq() : 0;
    setQualityOk(false);
  }, [side, isAssisted]);
  // The only true blockers on the shutter: a feed that has not shown real
  // content yet, or no light at all. On some iOS WebViews the feed
  // reports ready and stays black for 7s+ (one user uploaded a black
  // frame), so a frame counts as content once the whole-frame brightness
  // clears the dark-room floor. The hook's 10s manual fallback
  // (showManualCapture) lifts the content wait, never the dark one.
  const [feedHasContent, setFeedHasContent] = useState(false);
  useEffect(() => {
    setFeedHasContent(false);
  }, [side]);
  useEffect(() => {
    if (feedHasContent || !feedReady || isTooDark) return;
    const metrics = typeof getLastFrameMetrics === "function" ? getLastFrameMetrics() : null;
    if (metrics && metrics.frame_brightness != null) setFeedHasContent(true);
  }, [gatingState, isTooDark, feedReady, feedHasContent, getLastFrameMetrics]);
  const feedBlocked = isAssisted && (isTooDark || (!feedHasContent && !showManualCapture));
  // Time-boxed hold: the button comes back qualityHoldMaxMs after the
  // feed is up for this side, whatever the gates say. Adjustable through
  // the same layers as the thresholds (captureSettings.qualityHoldMaxMs).
  // The clock starts at the first frame with real content, not at
  // camera-ready, so a black feed never burns the hold.
  const qualityHoldMaxMs =
    typeof captureSettings.qualityHoldMaxMs === "number" ? captureSettings.qualityHoldMaxMs : QUALITY_HOLD_MAX_MS;
  const [holdExpired, setHoldExpired] = useState(false);
  useEffect(() => {
    setHoldExpired(false);
    if (!isAssisted || !feedReady || !feedHasContent) return undefined;
    const t = setTimeout(() => setHoldExpired(true), qualityHoldMaxMs);
    return () => clearTimeout(t);
  }, [side, isAssisted, feedReady, feedHasContent, qualityHoldMaxMs]);
  // Never release onto a dark frame: the cap unlocks the shutter for a
  // usable-but-imperfect frame, not for a covered lens or a black feed.
  const holdReleased = (holdExpired || showManualCapture) && !isTooDark;
  // The shutter waits for the gates (settled, see qualityOk) or the cap.
  const qualityHold = isAssisted && !qualityOk && !holdReleased;

  // Effective thresholds + where each came from, stamped on every
  // quality event so tuning sessions are distinguishable in the data.
  const qualityConfig = () => ({
    thresholds: {
      ...(typeof getThresholds === "function" ? getThresholds() : {}),
      hold_ms: qualityHoldMaxMs,
      settle_ms: CUE_SETTLE_MS,
      auto_arm_ms: AUTO_ARM_MS,
      auto_settle_ms: AUTO_SETTLE_MS,
      auto_blur_min: AUTO_BLUR_MIN,
      auto_glare_max: AUTO_GLARE_MAX,
      auto_luma_std_min: AUTO_LUMA_STD_MIN,
    },
    threshold_sources: captureSettings.qualitySources || {},
  });
  useEffect(() => {
    if (!isAssisted) return;
    emit("id_doc_quality_config", qualityConfig(), { once: true });
  }, [isAssisted]);

  useEffect(() => {
    if (!isAssisted || !qualityOk || qualityReadyAtRef.current) return;
    qualityReadyAtRef.current = performance.now();
    emit("id_doc_quality_gates_ready", {
      side,
      ready_ms: Math.round(qualityReadyAtRef.current - qualitySideStartRef.current),
      ...(typeof getLastFrameMetrics === "function" ? getLastFrameMetrics() : {}),
      ...qualityConfig(),
    });
  }, [qualityOk, isAssisted, side]);
  useEffect(() => {
    if (!isAssisted || !holdReleased || qualityOk) return;
    emit("id_doc_quality_gates_bypassed", {
      side,
      waited_ms: Math.round(performance.now() - qualitySideStartRef.current),
      blur: gatingState.blur,
      glare: gatingState.glare,
      lighting: gatingState.lighting,
      steady: gatingState.steady,
      too_dark: isTooDark,
      ...(typeof getLastFrameMetrics === "function" ? getLastFrameMetrics() : {}),
      ...qualityConfig(),
    });
  }, [holdReleased, isAssisted]);
  // Which gate to talk about, worst first. Only one message at a time.
  const qualityFeedbackKey = !isAssisted
    ? null
    : isTooDark
      ? "tooDark"
      : !gatingState.lighting
        ? "tooBright"
        : !gatingState.steady
          ? "holdStillMoving"
          : !gatingState.blur
            ? "tooBlurry"
            : !gatingState.glare
              ? "reduceGlare"
              : null;
  // Settled before it is shown: a key has to hold for CUE_SETTLE_MS (and
  // at least two frames) before the displayed cue changes, so a reading
  // that flickers around a threshold never flashes on screen. The dark
  // blocker is the exception and shows on the first frame.
  const [shownQualityKey, setShownQualityKey] = useState(null);
  const cueRunRef = useRef({ key: null, since: 0 });
  useEffect(() => {
    if (!isAssisted) return;
    const now = performance.now();
    const run = cueRunRef.current;
    if (run.key !== qualityFeedbackKey) {
      run.key = qualityFeedbackKey;
      run.since = now;
      if (qualityFeedbackKey === "tooDark") setShownQualityKey("tooDark");
      return;
    }
    if (now - run.since >= CUE_SETTLE_MS && shownQualityKey !== qualityFeedbackKey) setShownQualityKey(qualityFeedbackKey);
  }, [qualityFeedbackKey, gatingState, isAssisted]);
  useEffect(() => {
    cueRunRef.current = { key: null, since: 0 };
    setShownQualityKey(null);
  }, [side]);

  const qualityOkRef = useRef(qualityOk);
  qualityOkRef.current = qualityOk;
  const gatingRef = useRef(gatingState);
  gatingRef.current = gatingState;
  const tooDarkRef = useRef(isTooDark);
  tooDarkRef.current = isTooDark;

  // What fired the capture: "tap" (default) or "auto". The last one is
  // kept in state so the review screen can say the photo was taken
  // automatically — the one place auto-capture announces itself.
  const captureTriggerRef = useRef("tap");
  const [lastTrigger, setLastTrigger] = useState("tap");
  const suspendFrameLoop = useCallback((suspended) => {
    if (typeof setFrameLoopSuspended === "function") setFrameLoopSuspended(suspended);
  }, [setFrameLoopSuspended]);
  const releaseCapture = useCallback(() => {
    captureInFlightRef.current = null;
    setCapturing(false);
    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current);
      captureTimeoutRef.current = null;
    }
    suspendFrameLoop(false);
  }, [suspendFrameLoop]);
  useEffect(() => () => { if (captureTimeoutRef.current) clearTimeout(captureTimeoutRef.current); }, []);

  const handleCapture = useCallback(() => {
    if (!webcamRef.current) return;
    if (captureInFlightRef.current != null) {
      emit("id_doc_capture_deduped", { side, gap_ms: Math.round(performance.now() - captureInFlightRef.current) });
      return;
    }
    const tapAt = performance.now();
    captureInFlightRef.current = tapAt;
    // Each capture gets its own generation; a blob that lands after a
    // newer capture started is dropped, one that lands after the safety
    // timer merely re-enabled the shutter is still delivered.
    captureGenRef.current += 1;
    const gen = captureGenRef.current;
    setCapturing(true);
    suspendFrameLoop(true);
    captureTimeoutRef.current = setTimeout(() => {
      captureTimeoutRef.current = null;
      if (captureInFlightRef.current == null || captureGenRef.current !== gen) return;
      emit("id_doc_capture_timed_out", { side, waited_ms: CAPTURE_TIMEOUT_MS });
      releaseCapture();
    }, CAPTURE_TIMEOUT_MS);
    // Read and clear the trigger up front: the id_doc_captured emit runs
    // inside the async toBlob callback, by which time the auto loop had
    // already reset the ref to "tap" and every auto shot was stamped as a tap.
    const trigger = captureTriggerRef.current;
    captureTriggerRef.current = "tap";
    setLastTrigger(trigger);
    try {
      const video = webcamRef.current.video;
      if (!video) {
        releaseCapture();
        return;
      }

      // Notify parent synchronously BEFORE async toBlob — this lets
      // the parent set a guard ref so camera errors during the async
      // window won't reset the flow to setup.
      if (onCaptureStarted) onCaptureStarted();
      flash();

      const vWidth = video.videoWidth;
      const vHeight = video.videoHeight;
      const orientation = isLandscape ? "landscape" : "portrait";

      const frameChain = getFrameChain();
      if (vWidth > 0 && vHeight > 0) {
        // Reuse a dedicated canvas ref for capture to avoid leaking canvases
        let canvas = captureCanvasRef.current;
        if (!canvas) {
          canvas = document.createElement("canvas");
          captureCanvasRef.current = canvas;
        }

        // In stupid mode, crop to the guide box region so the ID fills
        // the output image instead of being a small portion of the full
        // frame. The crop is derived from the guide's on-screen rect so
        // it is correct in both orientations.
        let crop = null;
        if (isStupidMode && guideRef.current) {
          crop = guideRectToVideoCrop(
            video,
            video.getBoundingClientRect(),
            guideRef.current.getBoundingClientRect()
          );
        }

        // Best-frame capture (assisted mode, ID_DOC_BEST_FRAME). The best
        // gate-passing frame from the last two seconds replaces the
        // current one when it is sharper. Metrics are recorded either
        // way so the upload can be compared with what it replaced.
        let source = video;
        const bestFrameMetrics = {};
        if (isAssisted) {
          const bestFrameOn = flagEnabled(ID_DOC_BEST_FRAME, true);
          const best = typeof getBestFrame === "function" ? getBestFrame() : null;
          const currentSharpness = typeof measureCurrentSharpness === "function" ? measureCurrentSharpness() : null;
          bestFrameMetrics.best_frame_arm = bestFrameOn;
          bestFrameMetrics.current_frame_sharpness = currentSharpness == null ? null : Math.round(currentSharpness);
          if (best) {
            bestFrameMetrics.best_frame_sharpness = Math.round(best.sharpness);
            bestFrameMetrics.best_frame_luma = best.luma == null ? null : Math.round(best.luma);
            bestFrameMetrics.best_frame_age_ms = best.age_ms;
            bestFrameMetrics.frames_considered = best.considered;
            const bestIsBetter =
              best.sharpness != null &&
              (currentSharpness == null || best.sharpness > currentSharpness) &&
              best.canvas.width === vWidth &&
              best.canvas.height === vHeight;
            bestFrameMetrics.best_frame_used = bestFrameOn && bestIsBetter;
            if (bestFrameOn && bestIsBetter) source = best.canvas;
          } else {
            bestFrameMetrics.best_frame_used = false;
          }
        }

        if (crop) {
          canvas.width = crop.w;
          canvas.height = crop.h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(source, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);
        } else {
          // Non-stupid mode or fallback: capture full frame
          canvas.width = vWidth;
          canvas.height = vHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, vWidth, vHeight);
        }

        // Use toBlob instead of toDataURL — saves ~33% memory
        const syncMs = Math.round(performance.now() - tapAt);
        canvas.toBlob(
          (blob) => {
            // A newer capture has started since; this result is stale.
            if (captureGenRef.current !== gen) return;
            // The photo exists: the safety timer has nothing left to guard.
            if (captureTimeoutRef.current) {
              clearTimeout(captureTimeoutRef.current);
              captureTimeoutRef.current = null;
            }
            // Let the loop run again now that the encode is done; the guard
            // itself stays until the review / side change resolves.
            suspendFrameLoop(false);
            if (blob && onCapture) {
              emit("id_doc_captured", {
                side,
                doc_type: documentType,
                orientation,
                trigger,
                // Tap to blob: the wait the user sat through with the busy ring,
                // split into the synchronous part (frame draw, sharpness read,
                // best-frame pick) and the JPEG encode.
                encode_wait_ms: Math.round(performance.now() - tapAt),
                sync_ms: syncMs,
                encode_ms: Math.round(performance.now() - tapAt) - syncMs,
                ...(isAssisted
                  ? {
                      quality_ok: qualityOkRef.current,
                      quality_bypassed: !qualityOkRef.current,
                      blur: gatingRef.current.blur,
                      glare: gatingRef.current.glare,
                      lighting: gatingRef.current.lighting,
                      steady: gatingRef.current.steady,
                      too_dark: tooDarkRef.current,
                      ...(typeof getLastFrameMetrics === "function" ? getLastFrameMetrics() : {}),
                      ...bestFrameMetrics,
                      thresholds: { ...(typeof getThresholds === "function" ? getThresholds() : {}), hold_ms: qualityHoldMaxMs, settle_ms: CUE_SETTLE_MS },
                      threshold_sources: captureSettings.qualitySources || {},
                    }
                  : {}),
              });
              const previewUrl = URL.createObjectURL(blob);
              setCapturing(false);
              onCapture(blob, previewUrl, documentType, confidence, frameChain);
            } else {
              releaseCapture();
            }
          },
          "image/jpeg",
          ID_DOC_JPEG_QUALITY
        );
      } else {
        // Fallback: use react-webcam screenshot, convert data URL to blob directly
        // NOTE: Do NOT use fetch(dataUrl) — see face-capture/capture.js for details
        const imageSrc = webcamRef.current.getScreenshot();
        if (imageSrc && onCapture) {
          try {
            const [header, b64] = imageSrc.split(",");
            const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: mime });
            emit("id_doc_captured", { side, doc_type: documentType, fallback: "screenshot", orientation, trigger });
            const previewUrl = URL.createObjectURL(blob);
            suspendFrameLoop(false);
            setCapturing(false);
            onCapture(blob, previewUrl, documentType, confidence, frameChain);
          } catch (e) {
            releaseCapture();
            if (onError) onError({ type: "capture_error", message: "Failed to convert screenshot to blob" });
          }
        } else {
          releaseCapture();
        }
      }
    } catch (err) {
      releaseCapture();
      if (onError)
        onError({
          type: "capture_error",
          message: err?.message || "Screenshot capture failed",
        });
    }
  }, [onCapture, onCaptureStarted, onError, documentType, confidence, getFrameChain, getBestFrame, measureCurrentSharpness, getLastFrameMetrics, getThresholds, qualityHoldMaxMs, captureSettings.qualitySources, isStupidMode, isAssisted, side, isLandscape, flash, suspendFrameLoop, releaseCapture]);

  // Register auto-capture callback
  useEffect(() => {
    setOnAutoCapture(handleCapture);
  }, [handleCapture, setOnAutoCapture]);

  // Periodic passport analysis on live video (debug) — uses ref canvas to avoid leaking
  useEffect(() => {
    if (!cameraReady || isStupidMode) return;
    const interval = setInterval(() => {
      const video = webcamRef.current?.video;
      if (video && video.videoWidth > 0) {
        setPassportInfo(analyzePassportFromVideo(video, passportCanvasRef));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [cameraReady]);

  const handleUserMedia = useCallback(() => {
    console.log("[Capture] handleUserMedia fired — camera stream received");
    emit("id_doc_camera_permission_granted", {}, { once: true });
    cameraRespondedRef.current = true;
    const video = webcamRef.current?.video;
    if (!video) return;
    if (onCameraAllowed) onCameraAllowed();
    const checkReady = () => {
      if (
        video.videoWidth > 0 &&
        video.videoHeight > 0 &&
        video.readyState >= 2
      ) {
        emit("id_doc_camera_ready", { width: video.videoWidth, height: video.videoHeight, side });
        setCameraReady(true);
      } else {
        requestAnimationFrame(checkReady);
      }
    };
    requestAnimationFrame(checkReady);
  }, [onCameraAllowed, side]);

  const handleUserMediaError = useCallback(
    (err) => {
      console.error("[Capture] handleUserMediaError fired:", err?.name, err?.message, err);
      cameraRespondedRef.current = true;

      // NotReadableError means the camera is locked by another app (e.g. the
      // original webview that triggered the handoff). Retry once after a short
      // delay to give the other process time to release the camera.
      if (err?.name === "NotReadableError" && !retryAttemptedRef.current) {
        retryAttemptedRef.current = true;
        console.log("[Capture] NotReadableError — retrying getUserMedia in 2s");
        setTimeout(() => {
          cameraRespondedRef.current = false;
          navigator.mediaDevices
            .getUserMedia({
              video: {
                facingMode: "environment",
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
            })
            .then((stream) => {
              console.log("[Capture] Retry succeeded — attaching stream");
              emit("id_doc_camera_permission_granted", {}, { once: true });
              cameraRespondedRef.current = true;
              const video = webcamRef.current?.video;
              if (video) {
                video.srcObject = stream;
                video.play().catch(() => {});
                const checkReady = () => {
                  if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
                    emit("id_doc_camera_ready", { width: video.videoWidth, height: video.videoHeight, side });
                    setCameraReady(true);
                    if (onCameraAllowed) onCameraAllowed();
                  } else {
                    requestAnimationFrame(checkReady);
                  }
                };
                requestAnimationFrame(checkReady);
              }
            })
            .catch((retryErr) => {
              console.error("[Capture] Retry also failed:", retryErr?.name, retryErr?.message);
              emit("id_doc_camera_permission_denied", { name: retryErr?.name, error: retryErr?.message, retried: true });
              cameraRespondedRef.current = true;
              setCameraFailed(true);
              removeStyles();
              window.dispatchEvent(new CustomEvent("veratad-camera-error"));
              try {
                if (onError) {
                  onError({
                    type: "camera_error",
                    name: retryErr?.name || "UnknownError",
                    message: retryErr?.message || "Camera access failed",
                    constraint: retryErr?.constraint || null,
                  });
                }
              } catch (e) {
                console.error("[Capture] onError callback threw:", e);
              }
              if (typeof window.__veratadIdDocCameraError === "function") {
                window.__veratadIdDocCameraError();
              }
            });
          return;
        }, 2000);
        return;
      }

      emit("id_doc_camera_permission_denied", { name: err?.name, error: err?.message });

      // On Android, a NotAllowedError without user interaction likely means
      // the OS-level Camera toggle is blocking. Show specific instructions.
      if (shouldShowAndroidCameraBlock() && err?.name === "NotAllowedError") {
        console.warn("[Capture] Android NotAllowedError — likely OS camera toggle or Chrome permission blocked");
        setCameraBlocked(true);
        setAndroidOsBlock(true);
        removeStyles();

        window.dispatchEvent(new CustomEvent("veratad-camera-error"));
        try {
          if (onError) {
            onError({
              type: "camera_error",
              name: err?.name,
              message: err?.message || "Camera access blocked by device",
              constraint: err?.constraint || null,
            });
          }
        } catch (e) {
          console.error("[Capture] onError callback threw:", e);
        }
        if (typeof window.__veratadIdDocCameraError === "function") {
          window.__veratadIdDocCameraError();
        }
        return;
      }

      // Self-close: show error portal
      setCameraFailed(true);
      removeStyles();

      // Dispatch DOM event — most reliable way to reach the parent
      window.dispatchEvent(new CustomEvent("veratad-camera-error"));

      // Also try props callback and global as additional channels
      try {
        if (onError) {
          onError({
            type: "camera_error",
            name: err?.name || "UnknownError",
            message: err?.message || "Camera access failed",
            constraint: err?.constraint || null,
          });
        }
      } catch (e) {
        console.error("[Capture] onError callback threw:", e);
      }

      if (typeof window.__veratadIdDocCameraError === "function") {
        window.__veratadIdDocCameraError();
      }
    },
    [onError, onCameraAllowed, side]
  );

  // Android camera block: route to the shared portal which handles variant
  // Camera-fallback portal. Now renders for all devices via the generic
  // variant — Android users see the Quick Settings / Chrome permission
  // instructions below the primary button, non-Android users just see
  // the primary camera-app button and refresh. The state name
  // `androidOsBlock` is legacy from when this was Android-only.
  // Tap-to-focus: a tap on the feed (not on a control) asks the camera
  // for a single-shot focus at that point, then hands back to continuous.
  // Best effort: only Android Chrome exposes focusMode / pointsOfInterest;
  // everything else is a no-op reported in telemetry.
  const focusTimerRef = useRef(null);
  const handleStageTap = useCallback((e) => {
    if (e.target && e.target.closest && e.target.closest("button")) return;
    const video = videoElRef.current || webcamRef.current?.video;
    const stream = video?.srcObject;
    const track = stream && typeof stream.getVideoTracks === "function" ? stream.getVideoTracks()[0] : null;
    if (!track || typeof track.applyConstraints !== "function") return;
    let caps = null;
    try { caps = typeof track.getCapabilities === "function" ? track.getCapabilities() : null; } catch { caps = null; }
    const modes = (caps && caps.focusMode) || [];
    const canSingle = modes.includes("single-shot");
    const canPoints = !!(caps && caps.pointsOfInterest);
    const rect = video ? video.getBoundingClientRect() : null;
    const x = rect ? Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0.5;
    const y = rect ? Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)) : 0.5;
    emit("id_doc_tap_to_focus", { side, supported: canSingle, points: canPoints, x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 });
    if (!canSingle) return;
    const adv = canPoints ? { focusMode: "single-shot", pointsOfInterest: [{ x, y }] } : { focusMode: "single-shot" };
    track.applyConstraints({ advanced: [adv] }).catch(() => {});
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    focusTimerRef.current = setTimeout(() => {
      if (modes.includes("continuous")) track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
    }, 1500);
  }, [side]);
  useEffect(() => () => { if (focusTimerRef.current) clearTimeout(focusTimerRef.current); }, []);

  // Hooks below must run on every render, so they sit above the
  // early-return screens (Android block, camera blocked, camera failed).
  const isCombinedReview = !!(reviewImages && (reviewImages.front || reviewImages.back));
  const isReview = !!reviewImage || isCombinedReview;
  const manualAvailable = isStupidMode || showManualCapture || manualUnlocked;
  const shutterDisabled = !feedReady || captured || capturing || transitioning || isReview || !manualAvailable || feedBlocked || qualityHold;

  // Release the capture guard once the shot has been dealt with: the
  // review closed (accept or retake), the flow moved to the next side,
  // or a turn-over transition ended. Runs only on those edges, so the
  // window between the tap and the async toBlob callback stays guarded.
  useEffect(() => {
    if (!isReview && !transitioning) releaseCapture();
  }, [isReview, transitioning, side, releaseCapture]);

  // A short buzz on the gates-pass edge (Android; iOS ignores vibrate)
  // so the eye does not have to be on the button.
  useEffect(() => {
    if (!isAssisted || !qualityOk || captured || isReview) return;
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") navigator.vibrate(30);
    } catch {
      // not supported
    }
  }, [qualityOk, isAssisted, captured, isReview]);

  // ── Auto-capture (assisted, ID_DOC_AUTO_CAPTURE) ──
  // Eligible while every gate passes AND the presence heuristic says a
  // card fills the guide. Hold that for AUTO_ARM_MS + AUTO_SETTLE_MS and
  // handleCapture fires on its own. Nothing about it shows on screen
  // beyond the disclosure line under the shutter: the shutter keeps its
  // look, the cue keeps saying "Ready to take photo", and a tap at any
  // point takes the photo first. Any lapse cancels; the review step
  // stays as the safety net.
  const autoOn = isAssisted && flagEnabled(ID_DOC_AUTO_CAPTURE, true);
  // The frame also has to clear the auto bar (see AUTO_* above); the
  // manual shutter has no bar at all.
  const autoFrame = typeof getLastFrameMetrics === "function" ? getLastFrameMetrics() : null;
  const autoFrameOk =
    !!autoFrame &&
    autoFrame.blur_var != null && autoFrame.blur_var >= AUTO_BLUR_MIN &&
    ((autoFrame.glare_used ?? autoFrame.glare_ratio) == null || (autoFrame.glare_used ?? autoFrame.glare_ratio) < AUTO_GLARE_MAX) &&
    (autoFrame.luma_std == null || autoFrame.luma_std >= AUTO_LUMA_STD_MIN);
  const autoEligible =
    autoOn && qualityOk && autoFrameOk && !!gatingState.documentLikely && !shutterDisabled && !isReview && !transitioning && !captured;
  const autoStateRef = useRef({ startedAt: null, armed: false });
  useEffect(() => {
    const st = autoStateRef.current;
    if (!autoEligible) {
      if (st.armed) {
        emit("id_doc_auto_capture_cancelled", {
          side,
          remaining_ms: Math.max(0, Math.round(AUTO_ARM_MS + AUTO_SETTLE_MS - (performance.now() - st.startedAt))),
          ...(typeof getLastFrameMetrics === "function" ? getLastFrameMetrics() : {}),
        });
      }
      st.startedAt = null;
      st.armed = false;
      return undefined;
    }
    st.startedAt = performance.now();
    st.armed = false;
    const tick = setInterval(() => {
      const elapsed = performance.now() - st.startedAt;
      if (elapsed < AUTO_ARM_MS) return;
      if (!st.armed) {
        st.armed = true;
        emit("id_doc_auto_capture_armed", { side, ...(typeof getLastFrameMetrics === "function" ? getLastFrameMetrics() : {}) });
      }
      if (elapsed < AUTO_ARM_MS + AUTO_SETTLE_MS) return;
      clearInterval(tick);
      st.armed = false;
      st.startedAt = null;
      emit("id_doc_auto_capture_triggered", { side });
      try {
        if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") navigator.vibrate(60);
      } catch {
        // not supported
      }
      captureTriggerRef.current = "auto";
      handleCapture();
    }, 100);
    return () => clearInterval(tick);
  }, [autoEligible, side]);

  // The three "no camera" screens are picked here but returned at the very
  // end, after every hook, so a render that switches to one of them calls
  // exactly the same hooks as the render before it.
  let blockedScreen = null;
  if (androidOsBlock) {
    blockedScreen = (
      <AndroidCameraBlockPortal
        locale={locale}
        requestId={requestId}
        captureType="id_doc"
        isBrowserHandoffSession={isBrowserHandoffSession}
        androidVariant={getAndroidBlockVariant()}
        onPhotoFallback={onPhotoFallback}
      />
    );
  }

  // Generic browser-level camera blocked (non-Android handoff sessions).
  if (cameraBlocked) {
    const blockedPortal = (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          backgroundColor: "#000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <IconCameraOff size={64} color="rgba(255,255,255,0.7)" stroke={1.5} />
        <Text c="white" fz={22} fw={700} align="center" mt="lg">
          {locale?.errors?.cameraBlockedTitle || "Camera Access Blocked"}
        </Text>
        <Text c="rgba(255,255,255,0.7)" fz={14} align="center" mt="sm" maw={320}>
          {locale?.errors?.cameraBlockedMessage ||
            "Camera access is blocked in your browser. To enable it:"}
        </Text>
        <div style={{ textAlign: "start", maxWidth: 300, marginTop: 16 }}>
          <Text c="rgba(255,255,255,0.85)" fz={14} mb={8}>
            {locale?.errors?.cameraBlockedStep1 || "1. Tap the lock or settings icon in your browser's address bar"}
          </Text>
          <Text c="rgba(255,255,255,0.85)" fz={14} mb={8}>
            {locale?.errors?.cameraBlockedStep2 || "2. Find Camera permissions"}
          </Text>
          <Text c="rgba(255,255,255,0.85)" fz={14} mb={8}>
            {locale?.errors?.cameraBlockedStep3 || "3. Change to \"Allow\""}
          </Text>
          <Text c="rgba(255,255,255,0.85)" fz={14}>
            {locale?.errors?.cameraBlockedStep4 || "4. Come back and tap the button below"}
          </Text>
        </div>
        <Button
          variant="white"
          color="dark"
          size="md"
          mt="xl"
          onClick={() => {
            window.location.reload();
          }}
        >
          {locale?.errors?.tryAgainAfterUnblock || "Try Again"}
        </Button>
      </div>
    );
    if (!blockedScreen) blockedScreen = ReactDOM.createPortal(blockedPortal, document.body);
  }

  // Camera error: show error screen inside a portal
  if (cameraFailed) {
    const errorPortal = (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          backgroundColor: "#000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <IconCameraOff size={64} color="rgba(255,255,255,0.7)" stroke={1.5} />
        <Text c="white" fz={22} fw={700} align="center" mt="lg">
          {locale?.errors?.cameraErrorTitle || "Camera Access Denied"}
        </Text>
        <Text c="rgba(255,255,255,0.7)" fz={14} align="center" mt="sm" maw={320}>
          {locale?.errors?.cameraDenied ||
            "You have denied camera permissions. Please go into settings and allow camera, refresh the page, and try again."}
        </Text>
        <Button
          variant="white"
          color="dark"
          size="md"
          mt="xl"
          onClick={() => {
            setCameraFailed(false);
            window.dispatchEvent(new CustomEvent("veratad-camera-error"));
            try {
              if (onErrorRef.current) {
                onErrorRef.current({
                  type: "camera_error",
                  name: "NotAllowedError",
                  message: "Camera access denied by user",
                });
              }
            } catch (e) {
              console.error("[Capture] onError threw:", e);
            }
            if (typeof window.__veratadIdDocCameraError === "function") {
              window.__veratadIdDocCameraError();
            }
          }}
        >
          {locale?.errors?.goBack || "Go Back"}
        </Button>
      </div>
    );
    if (!blockedScreen) blockedScreen = ReactDOM.createPortal(errorPortal, document.body);
  }

  // ── Derived UI state ──

  const isBack = side === "back";
  const reviewIsBack = (reviewSide || side) === "back";

  const stepTemplate = locale?.capture?.stepOf || "Step {current} of {total}";

  const guideText = isBack
    ? locale?.capture?.guideBack || "Place the back of your ID inside the frame. Make sure the barcode is fully visible."
    : locale?.capture?.guideFront || "Place the front of your ID inside the frame, then tap Capture. Avoid glare and shadows.";

  const reviewTitle = reviewIsBack
    ? locale?.review?.checkBack || "Check the back of your ID"
    : locale?.review?.checkFront || "Check the front of your ID";
  const reviewSubtitle =
    locale?.review?.subtitle || "Make sure all details are readable, in focus and free of glare.";
  const usePhotoLabel = locale?.review?.usePhoto || "Use this photo";
  const usePhotosLabel = locale?.review?.usePhotos || "Use these photos";
  const retakeLabel = locale?.review?.retakeShort || "Retake";
  const checkBothTitle = locale?.review?.checkBoth || "Check your photos";
  const checkBothSubtitle = locale?.review?.subtitleBoth || "Make sure every detail is readable.";
  const frontLabel = locale?.capture?.frontTitle || "Front of ID";
  const backLabel = locale?.capture?.backTitle || "Back of ID";

  // The single actionable correction per gate, worst first (the order
  // qualityFeedbackKey already picks). One line, phrased as what to do.
  const fixText = {
    tooDark: locale?.capture?.fixTooDark || "Move somewhere brighter or turn on a light.",
    tooBright: locale?.capture?.fixTooBright || "Move out of direct light.",
    holdStillMoving: locale?.capture?.fixMoving || "Hold your phone still.",
    tooBlurry: locale?.capture?.fixBlurry || "Hold steady until the text looks sharp.",
    reduceGlare: locale?.capture?.fixGlare || "Tilt your ID to reduce glare.",
  };

  // ── The cue: one headline, one detail line, one tone ──
  // "You can take a photo" is the resting state whenever the shutter
  // works; "Ready to take photo" is the recommended moment; a blocker
  // headline appears only when the shutter is really held. Quality
  // problems short of a blocker stay in the detail line as advice.
  let cueHeadline = "";
  let cueDetail = "";
  let cueTone = "neutral";
  if (!feedReady) {
    cueHeadline = isStupidMode
      ? locale?.capture?.loadingCamera || "Starting camera..."
      : locale?.capture?.loadingModels || "Loading document scanner...";
    cueDetail = guideText;
  } else if (capturing) {
    // Between the tap and the encoded photo (master #424/#425): the
    // shutter shows a busy ring and takes no taps.
    cueHeadline = locale?.capture?.savingPhoto || "Saving your photo";
    cueTone = "ready";
  } else if (captured) {
    cueHeadline = locale?.capture?.cueCaptured || "Photo captured";
    cueTone = "ready";
  } else if (isAssisted) {
    if (isTooDark) {
      cueHeadline = locale?.feedback?.tooDark || "Not enough light";
      cueDetail = fixText.tooDark;
      cueTone = "blocked";
    } else if (!feedHasContent && !showManualCapture) {
      cueHeadline = locale?.capture?.loadingCamera || "Starting camera...";
      cueDetail = guideText;
    } else if (qualityOk) {
      cueHeadline = locale?.capture?.cueReady || "Ready to take photo";
      cueDetail = locale?.capture?.cueReadyDetail || "Your ID is clear and fully visible.";
      cueTone = "ready";
    } else if (holdReleased) {
      // Cap released with checks still failing: the button works, and the
      // detail line keeps offering the one correction that would help.
      cueHeadline = locale?.capture?.cueCanCapture || "You can take a photo";
      cueDetail = (shownQualityKey && fixText[shownQualityKey]) || guideText;
    } else {
      // Held: the headline says what to do, the detail says how. Both
      // come from strings every locale already has. Amber + "!" marks it
      // as the reason the shutter is waiting, not a passing status.
      cueHeadline = shownQualityKey
        ? locale?.feedback?.[shownQualityKey] || shownQualityKey
        : isBack
          ? locale?.capture?.centerBack || "Center the back of your ID"
          : locale?.capture?.centerFront || "Center the front of your ID";
      cueDetail = (shownQualityKey && fixText[shownQualityKey]) || guideText;
      cueTone = "blocked";
    }
  } else if (isStupidMode) {
    cueHeadline = locale?.capture?.cueCanCapture || "You can take a photo";
    cueDetail = guideText;
  } else {
    // Detector-driven mode: the hook's own (already debounced) message.
    cueHeadline = feedbackMessage;
    cueDetail = guideText;
    cueTone = isAllPassing ? "ready" : "neutral";
  }
  // Review: same block, review copy. The captured frame sits in the
  // guide where the feed was, and the shutter slot holds the actions.
  if (isCombinedReview) {
    // The header already says "Check your photos"; the block carries
    // only the instruction so nothing is said twice.
    cueHeadline = checkBothSubtitle;
    cueDetail = "";
    cueTone = "neutral";
  } else if (isReview) {
    cueHeadline = reviewTitle;
    cueDetail = reviewSubtitle;
    cueTone = "neutral";
  }
  // While the turn-over hint covers the feed: the capture confirmation
  // and the next step (the front was accepted without a review step in
  // the two-sided flow, so this is where "photo captured" is said).
  if (transitioning) {
    cueHeadline = locale?.capture?.cueCaptured || "Photo captured";
    cueDetail = locale?.capture?.nextBack || "Next, capture the back of your ID.";
    cueTone = "ready";
  }

  // Header follows the side under review while a review is up (the flow
  // may already have advanced `side` for the next capture).
  const headingIsBack = isCombinedReview || (isReview ? reviewIsBack : isBack);
  const headingTitle = isCombinedReview
    ? checkBothTitle
    : headingIsBack
      ? locale?.capture?.backTitle || "Back of ID"
      : locale?.capture?.frontTitle || "Front of ID";
  const headingStep = stepTemplate
    .replace("{current}", String(headingIsBack ? 2 : 1))
    .replace("{total}", String(totalSteps));

  const shutterLabel = locale?.capture?.captureButton || "Capture";
  // Under the shutter: while the gates hold it, why it is waiting; once it
  // is live, the one-time auto-capture disclosure (or nothing).
  const autoDisclosure = autoOn
    ? locale?.capture?.autoDisclosure || "A clear photo may be taken automatically."
    : "";
  const heldHint = locale?.capture?.cueHeldHint || "Unlocks when your ID is clear and steady.";
  const shutterHeld = isAssisted && feedReady && !captured && !isReview && !transitioning && (qualityHold || feedBlocked);
  const shutterSubline = shutterHeld ? heldHint : autoDisclosure;
  const shutterSublineTone = shutterHeld ? "warn" : "muted";
  const capturedAutomatically = locale?.capture?.capturedAutomatically || "Photo captured automatically";

  const bracketColor = transitioning
    ? "rgba(244,245,242,0.45)"
    : isReview || captured || (!isStupidMode && isAllPassing) || (isAssisted && qualityOk)
      ? readyColor
      : !isStupidMode && passingCount === 0 && !gatingState.documentPresent
        ? "rgba(244,245,242,0.55)"
        : glowColor;


  const handleHelp = () => {
    setHelpOpen(true);
    if (!isStupidMode) setManualUnlocked(true);
  };

  const showDebugPanel =
    (!isStupidMode || isAssisted) &&
    feedReady &&
    !captured &&
    new URLSearchParams(window.location.search).get("capture_debug") === "true";

  // ── Fit level for the translated copy ──
  // See LANDSCAPE_FIT / PORTRAIT_FIT. Landscape overflows when the side
  // panel (or the cue region inside it) has more content than height;
  // portrait when the footer has pushed the camera stage under
  // PORTRAIT_MIN_STAGE_H. Either is read off the DOM after the commit.
  const rootRef = useRef(null);
  const viewport = useViewportSize();
  const fitResetToken = useMemo(() => ({}), [isLandscape, viewport.width, viewport.height, locale]);
  const fitContentKey = [headingTitle, cueHeadline, cueDetail, shutterSubline, isReview, isCombinedReview, transitioning, lastTrigger].join("|");
  const fitLevel = useFitLevel({
    maxLevel: (isLandscape ? LANDSCAPE_FIT : PORTRAIT_FIT).length - 1,
    resetToken: fitResetToken,
    contentKey: fitContentKey,
    overflows: () => {
      const root = rootRef.current;
      if (!root) return false;
      const region = (name) => root.querySelector(`[data-capture-region="${name}"]`);
      if (isLandscape) {
        const panel = region("panel");
        const cueRegion = region("cue-region");
        const over = (el) => !!el && el.scrollHeight > el.clientHeight + 1;
        return over(panel) || over(cueRegion);
      }
      const stage = region("stage");
      const stageH = stage ? stage.getBoundingClientRect().height : 0;
      return stageH > 0 && stageH < PORTRAIT_MIN_STAGE_H;
    },
  });
  const fit = (isLandscape ? LANDSCAPE_FIT : PORTRAIT_FIT)[fitLevel];
  // Portrait minimums (see PORTRAIT_STAGE_FLOOR): the cue region keeps a
  // headline and two detail lines, the footer keeps that plus the
  // shutter slot, and the stage floor yields on a viewport too short
  // for all three.
  const cueMinH = Math.round(fit.headline * 1.25 + fit.detail * 1.4 * 2 + 6);
  const footerMinH = isLandscape ? 0 : cueMinH + fit.gap + fit.slotMinH + fit.padV;
  const stageFloor = isLandscape
    ? 0
    : Math.max(PORTRAIT_STAGE_FLOOR_MIN, Math.min(PORTRAIT_STAGE_FLOOR, (viewport.height || PORTRAIT_CHROME_H + PORTRAIT_STAGE_FLOOR) - PORTRAIT_CHROME_H));
  // Telemetry: one event per side when the design size did not fit, so
  // the languages / devices that need the smaller type are queryable.
  const fitReportedRef = useRef(null);
  useEffect(() => {
    if (fitLevel === 0) return;
    const key = `${side}:${isLandscape ? "l" : "p"}:${fitLevel}`;
    if (fitReportedRef.current === key) return;
    fitReportedRef.current = key;
    emit("id_doc_capture_fit_level", { side, level: fitLevel, language: settings?.language || null, viewport_w: viewport.width, viewport_h: viewport.height });
  }, [fitLevel, side, isLandscape]);

  // ── Shared fragments ──

  const videoLayer = preAcquiredStream ? (
    <video
      ref={videoElRef}
      autoPlay
      playsInline
      muted
      aria-hidden="true"
      tabIndex={-1}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        opacity: feedReady ? 1 : 0,
        transition: "opacity 0.3s ease-in",
      }}
    />
  ) : (
    <Webcam
      ref={webcamRef}
      audio={false}
      aria-hidden="true"
      tabIndex={-1}
      screenshotFormat="image/jpeg"
      screenshotQuality={ID_DOC_JPEG_QUALITY}
      playsInline
      videoConstraints={{
        facingMode: "environment",
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        aspectRatio: { ideal: 16 / 9 },
        // Android Chrome honours this; others ignore unknown advanced
        // constraints. Android fronts read half as sharp as iOS (median
        // blur 599 vs 2,288) and verified 16 points lower.
        advanced: [{ focusMode: "continuous" }],
      }}
      onUserMedia={handleUserMedia}
      onUserMediaError={handleUserMediaError}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        opacity: feedReady ? 1 : 0,
        transition: "opacity 0.3s ease-in",
      }}
    />
  );

  const guideFrame = (
    <div
      ref={guideRef}
      style={{
        position: "relative",
        width: guideWidth,
        height: guideHeight,
        flexShrink: 0,
        zIndex: 6,
      }}
    >
      <CornerBrackets color={bracketColor} size={bracketSize} radius={guideRadius} />
      {/* Capture flash — immediate feedback that the photo was taken */}
      {flashing && (
        <div
          style={{
            position: "absolute",
            inset: 6,
            borderRadius: 14,
            background: "#fff",
            animation: "docCaptureFlash 450ms ease-out forwards",
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );

  // Review layer: covers the whole stage (the live feed keeps running
  // underneath — the stream must stay alive for the next side — but is
  // fully hidden). Single side: the photo fills the stage. Two sides:
  // both photos, each labelled with its own Retake.
  const reviewTile = (src, label, sideKey) => (
    <div
      key={sideKey}
      style={{
        position: "relative",
        flex: "1 1 0",
        minWidth: 0,
        minHeight: 0,
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 2px", minWidth: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: FG, minWidth: 0, lineHeight: 1.25, ...WRAP_TEXT }}>{label}</span>
        <button
          type="button"
          onClick={() => onReviewRetakeSide && onReviewRetakeSide(sideKey)}
          style={{
            minHeight: 32,
            padding: "5px 14px",
            borderRadius: 16,
            border: "1.5px solid rgba(244,245,242,0.4)",
            background: "transparent",
            color: FG,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
            flexShrink: 0,
            maxWidth: "60%",
            ...WRAP_TEXT,
          }}
        >
          {retakeLabel}
        </button>
      </div>
      {/* Card-aspect box (the crop is the guide + padding, so ~1.6:1),
          bounded by the room it has, so the photo is not letterboxed. */}
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          aspectRatio: "1.45 / 1",
          maxHeight: "calc(100% - 44px)",
          alignSelf: "center",
          borderRadius: 14,
          background: STAGE_FALLBACK,
          overflow: "hidden",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.14)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {src ? (
          <img src={src} alt={label} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
        ) : null}
      </div>
    </div>
  );
  const reviewLayer = isReview && (
    <div
      data-capture-region="review"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 8,
        background: BG,
        display: "flex",
        flexDirection: isCombinedReview ? (isLandscape ? "row" : "column") : "column",
        alignItems: "stretch",
        justifyContent: "center",
        gap: 16,
        padding: isCombinedReview ? (isLandscape ? "16px 16px 16px 68px" : "12px 16px") : 0,
        boxSizing: "border-box",
      }}
    >
      {isCombinedReview ? (
        <>
          {reviewTile(reviewImages.front, frontLabel, "front")}
          {reviewTile(reviewImages.back, backLabel, "back")}
        </>
      ) : (
        <img
          src={reviewImage}
          alt={reviewTitle}
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      )}
    </div>
  );

  const loadingLayer = !feedReady && (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        pointerEvents: "none",
        zIndex: 15,
      }}
    >
      {/* Spinner only: the cue block already says what is loading, and one
          copy of the sentence is enough. */}
      <Loader size={36} color="white" />
      {!isStupidMode && (
        <div
          style={{
            fontSize: 10,
            fontFamily: MONO,
            color: "rgba(255,255,255,0.7)",
            textAlign: "center",
            lineHeight: 1.8,
            textShadow: "0 1px 3px rgba(0,0,0,0.9)",
          }}
        >
          <div>Camera: {cameraReady ? "✅" : "⏳ waiting..."}</div>
          <div>YOLO model: {modelsLoaded ? (modelLoadError ? "⚠️ failed" : "✅") : "⏳ loading..."}</div>
        </div>
      )}
    </div>
  );

  const debugPanel = showDebugPanel && (() => {
    const requiredGates = [
      { key: "documentPresent", label: "Document", pass: gatingState.documentPresent },
      { key: "classified", label: "Classified", pass: gatingState.classified },
    ];
    const qualityGates = [
      { key: "size", label: "Size", pass: gatingState.size },
      { key: "centering", label: "Centered", pass: gatingState.centering },
      { key: "blur", label: "Sharpness", pass: gatingState.blur },
      { key: "glare", label: "Glare", pass: gatingState.glare },
      { key: "lighting", label: "Lighting", pass: gatingState.lighting },
    ];
    const yoloOk = requiredGates.every((g) => g.pass);
    const qualityPassing = qualityGates.filter((g) => g.pass).length;
    const readyToCapture = yoloOk && qualityPassing >= 3;
    const row = (g) => (
      <div
        key={g.key}
        style={{
          backgroundColor: g.pass ? "rgba(34,139,34,0.75)" : "rgba(180,30,30,0.75)",
          borderRadius: 6,
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: MONO,
          fontSize: 11,
          color: "#fff",
          fontWeight: 600,
        }}
      >
        <span>{g.pass ? "✅" : "❌"}</span>
        <span>{g.label}</span>
      </div>
    );
    return (
      <div
        style={{
          position: "absolute",
          top: 12,
          insetInlineEnd: 12,
          zIndex: 30,
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          minWidth: 150,
          maxHeight: "calc(100% - 24px)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            backgroundColor: readyToCapture ? "rgba(34,139,34,0.85)" : "rgba(0,0,0,0.75)",
            borderRadius: 8,
            padding: "6px 10px",
            textAlign: "center",
            fontFamily: MONO,
            fontSize: 11,
            fontWeight: 700,
            color: "#fff",
            letterSpacing: "0.05em",
          }}
        >
          {readyToCapture ? "READY TO CAPTURE" : `YOLO: ${yoloOk ? "OK" : "WAIT"} | Quality: ${qualityPassing}/5 (need 3)`}
        </div>
        {requiredGates.map(row)}
        {qualityGates.map(row)}
        <div
          style={{
            backgroundColor: "rgba(0,0,0,0.65)",
            borderRadius: 6,
            padding: "5px 10px",
            fontFamily: MONO,
            fontSize: 10,
            color: "rgba(255,255,255,0.8)",
            lineHeight: 1.6,
          }}
        >
          <div>type: {documentType || "none"} @ {(confidence * 100).toFixed(0)}%</div>
          <div>area: {(areaRatio * 100).toFixed(1)}%</div>
          <div>model: {modelLoadError ? "none" : "YOLO"}</div>
          <div>strategy: {captureStrategy} | misses: {yoloMissCount}</div>
          {passportInfo && (
            <div>mrz: {passportInfo.mrzDetected ? "yes" : "no"} | ar: {passportInfo.aspectRatio}</div>
          )}
          {yoloDetail && (
            <div style={{ color: yoloDetail.source === "yolo" ? "#69db7c" : "#ffd43b", fontWeight: 600, marginTop: 2 }}>
              {yoloDetail.source} | {yoloDetail.totalDetections} det {yoloDetail.dims && `| ${yoloDetail.dims}`}
            </div>
          )}
        </div>
      </div>
    );
  })();

  const reviewActions = (
    <div data-capture-region="review-actions" style={{ width: "100%", maxWidth: 340, minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
      <PrimaryButton onClick={onReviewAccept} accent={ctaColor} accentText={onAccent} compact={isLandscape}>
        {isCombinedReview ? usePhotosLabel : usePhotoLabel}
      </PrimaryButton>
      {!isCombinedReview && (
        <SecondaryButton onClick={onReviewRetake} compact={isLandscape}>
          {retakeLabel}
        </SecondaryButton>
      )}
      {lastTrigger === "auto" && (
        <span style={{ fontSize: isLandscape ? 12 : 13, lineHeight: 1.3, color: MUTED, textAlign: "center", ...WRAP_BODY }}>
          {capturedAutomatically}
        </span>
      )}
    </div>
  );

  // ── Layouts ──

  const rootStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: BG,
    color: FG,
    overflow: "hidden",
    display: "flex",
    flexDirection: isLandscape ? "row" : "column",
    boxSizing: "border-box",
    paddingTop: "env(safe-area-inset-top)",
    paddingRight: "env(safe-area-inset-right)",
    paddingBottom: "env(safe-area-inset-bottom)",
    paddingLeft: "env(safe-area-inset-left)",
    WebkitTapHighlightColor: "transparent",
  };

  const stageStyle = {
    position: "relative",
    flexGrow: 1,
    minWidth: 0,
    // Portrait: the stage gives way to a long footer only down to the
    // floor; the footer's cue region scrolls past that.
    minHeight: stageFloor,
    background: STAGE_FALLBACK,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    boxSizing: "border-box",
  };

  const closeButton = onClose && (
    <RoundControl label={locale?.capture?.close || "Close"} onClick={onClose} translucent={isLandscape}
      style={isLandscape ? { position: "absolute", zIndex: 45, insetInlineStart: 12, top: 12 } : undefined}>
      <IconX size={20} stroke={2} aria-hidden="true" />
    </RoundControl>
  );

  const helpButton = (
    <RoundControl label={locale?.capture?.help || "Help"} onClick={handleHelp} translucent={isLandscape} region="help-button"
      style={isLandscape ? { position: "absolute", zIndex: 45, insetInlineStart: 12, bottom: 12 } : undefined}>
      <IconHelp size={20} stroke={2} aria-hidden="true" />
    </RoundControl>
  );

  let content;
  if (isLandscape) {
    content = (
      <>
        {/* Camera stage — feed, frame, overlaid controls. Keyed (as is
            every top-level child of both layouts) so a rotation moves the
            existing stage — and the playing <video> inside it — instead of
            rebuilding it by position. A rebuilt stage re-requests the camera,
            and iOS Safari paints the second stream black. */}
        <div key="stage" ref={stageRef} data-capture-region="stage" style={stageStyle} onClick={handleStageTap}>
          {videoLayer}
          {reviewLayer}
          {closeButton}
          {!isReview && helpButton}
          {!isReview && guideFrame}
          {loadingLayer}
          {debugPanel}
          {transitioning && <TurnOverlay accent={turnHintColor} compact />}
        </div>

        {/* Side panel: heading pinned at the top, shutter (or the review
            actions) pinned at the bottom, the cue in between. The cue
            region is the only part that can scroll, so however long the
            translation, the heading and the shutter stay where they are
            and nothing is drawn over anything else. The type steps down
            (fit, above) before it comes to scrolling. */}
        <div
          key="panel"
          data-capture-region="panel"
          style={{
            width: SIDE_PANEL_WIDTH_CSS,
            flexShrink: 0,
            minHeight: 0,
            boxSizing: "border-box",
            padding: `${fit.padY}px ${fit.padX}px`,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: fit.gap,
            background: BG,
            overflowY: "auto",
          }}
        >
          <StepHeading title={headingTitle} stepText={headingStep} totalSteps={totalSteps} isBack={headingIsBack} accent={stepColor} readyColor={readyColor}size={fit.heading} pinned />
          {/* Centered while there is room, top-aligned and scrollable (with
              a fade saying so) when there is not — never overflowing its box. */}
          <ScrollRegion region="cue-region" center contentKey={fitContentKey} style={{ flex: "1 1 0", width: "100%" }}>
            <CueBlock
              headline={cueHeadline}
              detail={cueDetail}
              tone={cueTone}
              readyColor={readyColor}
              warnColor={warnColor}
              compact
              headlineSize={fit.headline}
              detailSize={fit.detail}
              detailLines={0}
            />
          </ScrollRegion>
          {/* Same slot for the shutter and for the review actions, at
              one height, so the panel does not move between the two. */}
          <div
            data-capture-region="action-slot"
            style={{ width: "100%", minHeight: fit.slotMinH || undefined, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            {isReview ? (
              reviewActions
            ) : (
              <ShutterGroup
                onClick={handleCapture}
                disabled={shutterDisabled}
                size={fit.shutter}
                label={shutterLabel}
                subline={shutterSubline}
                sublineTone={shutterSublineTone}
                warnColor={warnColor}
                busy={capturing}
                busyLabel={locale?.capture?.savingPhoto || "Saving your photo"}
                compact
                labelSize={fit.label}
                sublineSize={fit.subline}
                gap={fit.shutterGap}
                reserveSubline={fit.reserveSubline}
              />
            )}
          </div>
        </div>
      </>
    );
  } else {
    content = (
      <>
        {/* Header */}
        <div
          key="header"
          data-capture-region="header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            padding: fitLevel === 0 ? "14px 12px 10px" : "10px 12px 8px",
            flexShrink: 0,
          }}
        >
          {closeButton || <div style={{ width: CONTROL_SIZE, flexShrink: 0 }} />}
          <StepHeading title={headingTitle} stepText={headingStep} totalSteps={totalSteps} isBack={headingIsBack} accent={stepColor} readyColor={readyColor}size={fit.heading} />
          {isReview ? <div style={{ width: CONTROL_SIZE, flexShrink: 0 }} /> : helpButton}
        </div>

        {/* Camera stage (same key as the landscape stage — see there) */}
        {/* The stage is only the camera: feed + guide frame. Every word of
            guidance lives in the footer, in a block that never changes
            size, so the frame stays put while the cue changes. */}
        <div key="stage" ref={stageRef} data-capture-region="stage" style={stageStyle} onClick={handleStageTap}>
          {videoLayer}
          {reviewLayer}
          {!isReview && guideFrame}
          {loadingLayer}
          {debugPanel}
          {transitioning && <TurnOverlay accent={turnHintColor} />}
        </div>

        {/* Footer: content-sized, but once the stage is at its floor the
            footer gives way instead, and it is the cue region that
            shrinks and scrolls — the shutter slot keeps its height. */}
        <div
          key="footer"
          data-capture-region="footer"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: fit.gap,
            padding: fit.pad,
            background: BG,
            flexShrink: 1,
            minHeight: footerMinH,
          }}
        >
          <ScrollRegion region="cue-region" contentKey={fitContentKey} style={{ flex: "0 1 auto", minHeight: cueMinH, width: "100%" }}>
            <CueBlock
              headline={cueHeadline}
              detail={cueDetail}
              tone={cueTone}
              readyColor={readyColor}
              warnColor={warnColor}
              headlineSize={fit.headline}
              detailSize={fit.detail}
              detailLines={fit.detailLines}
            />
          </ScrollRegion>
          <div
            data-capture-region="action-slot"
            style={{ width: "100%", minHeight: fit.slotMinH, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            {isReview ? (
              reviewActions
            ) : (
              <ShutterGroup
                onClick={handleCapture}
                disabled={shutterDisabled}
                size={fit.shutter}
                label={shutterLabel}
                subline={shutterSubline}
                sublineTone={shutterSublineTone}
                warnColor={warnColor}
                busy={capturing}
                busyLabel={locale?.capture?.savingPhoto || "Saving your photo"}
                labelSize={fit.label}
                sublineSize={fit.subline}
                gap={fit.shutterGap}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  if (blockedScreen) return blockedScreen;
  // `lang` lets the browser hyphenate (WRAP_TEXT) and pick the right
  // glyphs for the session language; the app does not set it on <html>.
  // The help sheet covers the whole screen, not just the camera stage:
  // on a short phone in portrait the stage can be under 250px tall and
  // the sheet (with its "Got it" button) did not fit inside it.
  return ReactDOM.createPortal(
    <div ref={rootRef} data-capture-region="root" data-fit-level={fitLevel} lang={settings?.language ? String(settings.language).replace(/_/g, "-") : undefined} style={rootStyle}>
      {content}
      {helpOpen && (
        <HelpSheet
          key="help"
          locale={locale}
          accent={guidanceIconColor}
          buttonColor={ctaColor}
          accentText={onAccent}
          onClose={() => setHelpOpen(false)}
          compact={viewport.height > 0 && viewport.height < HELP_SHEET_COMPACT_H}
        />
      )}
    </div>,
    document.body
  );
}
